import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  FiCheckCircle,
  FiCreditCard,
  FiLock,
  FiFileText,
  FiDollarSign,
  FiArrowRight,
  FiMapPin,
  FiChevronDown,
  FiSearch,
  FiPhone,
  FiMail,
  FiHome,
  FiUser,
} from 'react-icons/fi';
import { LuIndianRupee } from 'react-icons/lu';
import {
  loanBackendUrl,
  saveLendingPageUrl,
  taskApiDirectUrl,
  taskApiPostUrl,
  getDigitapKycDetailsUrl,
  crossVerticalPanUrl,
  formatApiMessage,
  isLeadSaveSuccess,
  getLeadSaveMessage,
  getLeadInsertedId,
  isMobileOtpSendSuccess,
  isMobileOtpVerifySuccess,
  getMobileOtpMessage,
  mobileOtpUrl,
  buildMobileOtpBody,
  mobileOtpRequestHeaders,
} from '../config/taskApi';
import { getCitiesForState } from '../utils/stateCityMapping';
import applyNowImg from '../assets/images/apply-now.png';
import './apply-mrrupya.css';

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const AADHAAR_REGEX = /^\d{12}$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PINCODE_REGEX = /^[1-9]\d{5}$/;
const ONLY_DIGITS = /^\d+$/;
const PAN_VERIFY_STORAGE_KEY = 'mrrupya_verify_pan_cache';
const APPLY_LOAN_DRAFT_KEY = 'mrrupya_apply_loan_draft';
const APPLY_LOAN_DRAFT_VERSION = 1;
const DIGITAP_PENDING_TXN_KEY = 'mrrupya_digitap_pending_txn';
const DIGITAP_CALLBACK_DONE_PREFIX = 'mrrupya_aadhaar_cb_done_';
const DIGITAP_CALLBACK_TXN_KEY = 'mrrupya_digitap_callback_txn';
const DIGITAP_SESSION_MAX_AGE_MS = 30 * 60 * 1000;

const normalizeDigitapTransactionId = (value) =>
  String(value || '')
    .trim()
    .replace(/[{}]/g, '')
    .replace(/\D/g, '');

const isValidDigitapTransactionId = (value) =>
  /^\d{10,30}$/.test(normalizeDigitapTransactionId(value));

const getBrowserSearch = () => {
  if (typeof window === 'undefined') return '';
  return window.location.search || '';
};

const parseDigitapCallbackParams = (search) => {
  const params = new URLSearchParams(search || '');
  const txnId = normalizeDigitapTransactionId(
    params.get('txnId') ||
      params.get('txn_id') ||
      params.get('transactionId') ||
      params.get('transaction_id') ||
      ''
  );
  const aadhaarVerified =
    params.get('aadhaarVerified') === '1' ||
    params.get('aadhaar_verified') === '1' ||
    params.get('success') === 'true' ||
    params.get('success') === '1';
  const failed =
    params.get('aadhaarVerified') === '0' ||
    params.get('aadhaar_verified') === '0' ||
    params.get('success') === 'false' ||
    params.get('success') === '0';
  return { txnId, aadhaarVerified, failed };
};

const scrubApplyLoanUrl = () => {
  if (typeof window === 'undefined') return;
  window.history.replaceState({}, document.title, window.location.pathname);
};

const persistDigitapCallbackTxn = (txnId) => {
  if (!isValidDigitapTransactionId(txnId)) return;
  try {
    sessionStorage.setItem(
      DIGITAP_CALLBACK_TXN_KEY,
      JSON.stringify({
        transactionId: normalizeDigitapTransactionId(txnId),
        aadhaarVerified: true,
        savedAt: Date.now(),
      })
    );
  } catch {
    // ignore private mode
  }
};

const readDigitapCallbackTxn = () => {
  try {
    const raw = sessionStorage.getItem(DIGITAP_CALLBACK_TXN_KEY);
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    if (!parsed?.aadhaarVerified || !parsed?.transactionId) return '';
    if (Date.now() - Number(parsed.savedAt || 0) > DIGITAP_SESSION_MAX_AGE_MS) {
      sessionStorage.removeItem(DIGITAP_CALLBACK_TXN_KEY);
      return '';
    }
    return normalizeDigitapTransactionId(parsed.transactionId);
  } catch {
    return '';
  }
};

const clearDigitapCallbackTxn = () => {
  try {
    sessionStorage.removeItem(DIGITAP_CALLBACK_TXN_KEY);
  } catch {
    // ignore
  }
};

const resolveDigitapCallbackTxn = (search = '') => {
  const fromUrl = parseDigitapCallbackParams(search);
  if (fromUrl.failed) {
    return { txnId: '', aadhaarVerified: false, failed: true };
  }
  if (fromUrl.txnId && fromUrl.aadhaarVerified && isValidDigitapTransactionId(fromUrl.txnId)) {
    persistDigitapCallbackTxn(fromUrl.txnId);
    return {
      txnId: normalizeDigitapTransactionId(fromUrl.txnId),
      aadhaarVerified: true,
      failed: false,
    };
  }
  const storedTxnId = readDigitapCallbackTxn();
  if (storedTxnId && isValidDigitapTransactionId(storedTxnId)) {
    return { txnId: storedTxnId, aadhaarVerified: true, failed: false };
  }
  return { txnId: '', aadhaarVerified: false, failed: false };
};

const markDigitapPendingTxn = (transactionId) => {
  try {
    localStorage.setItem(
      DIGITAP_PENDING_TXN_KEY,
      JSON.stringify({
        transactionId: String(transactionId),
        savedAt: Date.now(),
      })
    );
  } catch {
    // ignore quota / private mode
  }
};

const readDigitapPendingTxn = () => {
  try {
    const raw = localStorage.getItem(DIGITAP_PENDING_TXN_KEY);
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    if (!parsed?.transactionId) return '';
    if (Date.now() - Number(parsed.savedAt || 0) > DIGITAP_SESSION_MAX_AGE_MS) {
      localStorage.removeItem(DIGITAP_PENDING_TXN_KEY);
      return '';
    }
    return String(parsed.transactionId);
  } catch {
    return '';
  }
};

const clearDigitapPendingTxn = () => {
  try {
    localStorage.removeItem(DIGITAP_PENDING_TXN_KEY);
  } catch {
    // ignore
  }
};

const isDigitapCallbackProcessed = (txnId) => {
  try {
    return localStorage.getItem(`${DIGITAP_CALLBACK_DONE_PREFIX}${txnId}`) === '1';
  } catch {
    return false;
  }
};

const markDigitapCallbackProcessed = (txnId) => {
  try {
    localStorage.setItem(`${DIGITAP_CALLBACK_DONE_PREFIX}${txnId}`, '1');
    clearDigitapPendingTxn();
    clearDigitapCallbackTxn();
  } catch {
    // ignore
  }
};

const readStoredPanVerify = (pan) => {
  try {
    const raw = localStorage.getItem(PAN_VERIFY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || String(parsed.pan || '').toUpperCase() !== String(pan || '').toUpperCase()) {
      return null;
    }
    return parsed.response || null;
  } catch {
    return null;
  }
};

const writeStoredPanVerify = (pan, response) => {
  try {
    if (!pan || !response || response.failed) return;
    localStorage.setItem(
      PAN_VERIFY_STORAGE_KEY,
      JSON.stringify({
        pan: String(pan).toUpperCase(),
        response,
        savedAt: Date.now(),
      })
    );
  } catch {
    // ignore quota / private mode
  }
};

const clearStoredPanVerify = () => {
  try {
    localStorage.removeItem(PAN_VERIFY_STORAGE_KEY);
  } catch {
    // ignore
  }
};

const readApplyLoanDraft = () => {
  try {
    const raw = localStorage.getItem(APPLY_LOAN_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== APPLY_LOAN_DRAFT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeApplyLoanDraft = (draft) => {
  try {
    localStorage.setItem(
      APPLY_LOAN_DRAFT_KEY,
      JSON.stringify({
        ...draft,
        version: APPLY_LOAN_DRAFT_VERSION,
        savedAt: Date.now(),
      })
    );
  } catch {
    // ignore quota / private mode
  }
};

const clearApplyLoanDraft = () => {
  try {
    localStorage.removeItem(APPLY_LOAN_DRAFT_KEY);
  } catch {
    // ignore
  }
};

/** Submit success only — do not call mid-flow or DigiLocker resume will break. */
const clearApplyLoanSessionAfterSubmit = (txnId = '') => {
  clearApplyLoanDraft();
  clearStoredPanVerify();
  clearDigitapPendingTxn();
  clearDigitapCallbackTxn();
  const id = normalizeDigitapTransactionId(txnId);
  if (!id) return;
  try {
    localStorage.removeItem(`${DIGITAP_CALLBACK_DONE_PREFIX}${id}`);
  } catch {
    // ignore
  }
};

const formatIndianCurrency = (value) => {
  const num = Number(String(value).replace(/,/g, ''));
  if (Number.isNaN(num)) return value;
  return new Intl.NumberFormat('en-IN').format(num);
};

const maskMobileNumber = (mobile) => {
  const digits = String(mobile).replace(/\D/g, '');
  if (digits.length !== 10) return mobile;
  return `${digits.slice(0, 2)}****${digits.slice(-4)}`;
};

const SUCCESS_STEPS = [
  {
    title: 'Application review',
    text: 'Our team verifies your details within a few working hours.',
  },
  {
    title: 'Call from our expert',
    text: 'We contact you on your registered mobile number with the next steps.',
  },
  {
    title: 'Quick disbursal',
    text: 'Once approved, funds are transferred directly to your bank account.',
  },
];

const MIN_LOAN_AMOUNT = 10000;
const MAX_LOAN_AMOUNT = 100000;
const MIN_MONTHLY_INCOME = 50000;
const MAX_MONTHLY_INCOME = 5000000;
const MAX_OBLIGATIONS = 5000000;
const MIN_ADDRESS_LENGTH = 15;
const MAX_ADDRESS_LENGTH = 500;
const MAX_EMAIL_LENGTH = 254;
const OTP_RESEND_SECONDS = 60;
const AADHAAR_OTP_SEND_SUCCESS = 'SOS174';
const AADHAAR_OTP_VERIFY_SUCCESS = 'SRC001';
const DIGITAP_POLL_MS = 3000;
const DIGITAP_POLL_MAX_MS = 5 * 60 * 1000;

const getDigitapApiMessage = (data, fallback = 'Something went wrong. Please try again.') => {
  const top = data?.message || data?.error || '';
  const nested =
    data?.data?.msg ||
    data?.data?.message ||
    data?.data?.digitapResponse?.msg ||
    data?.digitap_response?.msg ||
    '';
  if (top && nested && top !== nested) {
    return formatApiMessage(`${top} — ${nested}`);
  }
  return formatApiMessage(String(top || nested || fallback));
};

const parseMaybeJson = (value) => {
  if (value == null) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

const unwrapDigitapPayload = (raw) => {
  const data = parseMaybeJson(raw) || raw;
  if (!data || typeof data !== 'object') return data;

  const digitapResponse =
    parseMaybeJson(data.digitap_response) ||
    parseMaybeJson(data.digitapResponse) ||
    data.digitap_response ||
    data.digitapResponse;

  if (digitapResponse && typeof digitapResponse === 'object') {
    return {
      ...data,
      digitap_response: digitapResponse,
      digitapResponse,
    };
  }

  return data;
};

const deepFindDigitapValue = (obj, keys, depth = 0) => {
  if (!obj || typeof obj !== 'object' || depth > 6) return '';
  for (const key of keys) {
    const value = obj[key];
    if (value != null && String(value).trim()) {
      return String(value).trim();
    }
  }
  for (const value of Object.values(obj)) {
    if (value && typeof value === 'object') {
      const found = deepFindDigitapValue(value, keys, depth + 1);
      if (found) return found;
    }
  }
  return '';
};

const getDigitapKycUrl = (raw) => {
  const data = unwrapDigitapPayload(raw);
  if (!data || typeof data !== 'object') return '';

  const digitapResponse = data.digitap_response || data.digitapResponse;
  const model = data.model || digitapResponse?.model || data.data?.model;

  const direct =
    model?.kycUrl ||
    model?.url ||
    digitapResponse?.kycUrl ||
    digitapResponse?.url ||
    data.kycUrl ||
    data.url ||
    data.data?.kycUrl ||
    data.data?.url ||
    '';

  if (direct) return String(direct).trim();
  return deepFindDigitapValue(data, ['kycUrl', 'url']);
};

const getDigitapTransactionId = (raw) => {
  const data = unwrapDigitapPayload(raw);
  if (!data || typeof data !== 'object') return '';

  const digitapResponse = data.digitap_response || data.digitapResponse;
  const model = data.model || digitapResponse?.model || data.data?.model;

  const direct =
    model?.transactionId ||
    model?.transaction_id ||
    digitapResponse?.transactionId ||
    digitapResponse?.transaction_id ||
    data.transactionId ||
    data.transaction_id ||
    data.data?.transactionId ||
    data.data?.transaction_id ||
    '';

  if (direct) return normalizeDigitapTransactionId(direct);
  return normalizeDigitapTransactionId(
    deepFindDigitapValue(data, ['transactionId', 'transaction_id', 'txnId', 'txn_id'])
  );
};

const hasDigitapKycUrl = (raw) => Boolean(getDigitapKycUrl(raw));

const extractDigitapProfile = (data) => {
  if (!data || typeof data !== 'object') return null;

  const taskApiProfile = mapTaskApiDigitapProfile(data);
  if (taskApiProfile) return taskApiProfile;

  const digitapModel =
    data?.digitap_response?.model ||
    data?.digitapResponse?.model ||
    data?.model;
  if (
    digitapModel &&
    typeof digitapModel === 'object' &&
    (digitapModel.name || digitapModel.customer_name || digitapModel.dob)
  ) {
    return {
      ...digitapModel,
      dob: normalizeDigitapDob(digitapModel.dob || digitapModel.dateOfBirth),
      dateOfBirth: normalizeDigitapDob(digitapModel.dob || digitapModel.dateOfBirth),
      maskAadhaarNumber:
        digitapModel.maskAadhaarNumber ||
        digitapModel.maskedAadhaarNumber ||
        digitapModel.maskedAdharNumber ||
        digitapModel.masked_aadhaar_number ||
        '',
    };
  }

  const candidates = [
    data.data,
    data.data?.data,
    data.profile,
    data.data?.profile,
    data.data?.aadhaarData,
    data.aadhaarData,
  ];
  for (const candidate of candidates) {
    if (
      candidate &&
      typeof candidate === 'object' &&
      (candidate.name ||
        candidate.fullName ||
        candidate.firstName ||
        candidate.customer_name ||
        candidate.documentType ||
        candidate.dateOfBirth ||
        candidate.dob)
    ) {
      return candidate;
    }
  }
  return normalizeKycApiResponse(data).data;
};

const isDigitapKycPending = (data) => {
  const status = String(data?.Status || data?.status || '').toLowerCase();
  const message = String(data?.Message || data?.message || '').toLowerCase();
  return status === 'failed' && message.includes('not found');
};

const hasDigitapKycIdentity = (row) => {
  if (!row || typeof row !== 'object') return false;
  return Boolean(
    row.customer_name ||
      row.name ||
      row.masked_aadhaar_number ||
      row.masked_aadhaar ||
      row.dob ||
      row.gender
  );
};

const isDigitapKycComplete = (data) => {
  if (!data || typeof data !== 'object') return false;

  const row = data?.Data || data?.data;
  const status = String(data?.Status || data?.status || '').toLowerCase();
  if (status === 'success' && hasDigitapKycIdentity(row)) {
    return true;
  }

  const verificationStatus = String(
    row?.verification_status || ''
  ).toLowerCase();
  if (verificationStatus === 's' && hasDigitapKycIdentity(row)) {
    return true;
  }

  if (extractDigitapProfile(data)) return true;
  if (data.status === true && (data.data || data.profile)) return true;
  if (data.success === true && (data.data || data.profile)) return true;
  const normalized = normalizeKycApiResponse(data);
  if (normalized.responseCode === AADHAAR_OTP_VERIFY_SUCCESS && normalized.data) return true;
  const code = String(
    data?.data?.code || data?.data?.status || data?.status_code || data?.responseCode || ''
  ).toLowerCase();
  return ['complete', 'completed', 'success', 'verified', 'src001'].some((key) => code.includes(key));
};

const formatCountdown = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

/**
 * mrrupya.in sometimes wraps KYC as:
 * { success, status_code, data: { responseCode, responseMessage, referenceId, data?/profile fields } }
 * LoanOnSalary style is flat: { responseCode, responseMessage, data, referenceId }
 */
const normalizeKycApiResponse = (raw) => {
  if (!raw || typeof raw !== 'object') return {};

  // Flat success/error (responseCode at root)
  if (raw.responseCode || raw.responseMessage) {
    return {
      responseCode: String(raw.responseCode || ''),
      responseMessage: raw.responseMessage || raw.message || '',
      referenceId: raw.referenceId || '',
      data: raw.data || null,
      success: raw.success,
      status: raw.status,
      failed: raw.failed,
    };
  }

  const inner = raw.data;
  if (inner && typeof inner === 'object') {
    const responseCode = String(inner.responseCode || raw.responseCode || '');
    const responseMessage =
      inner.responseMessage ||
      inner.message ||
      raw.responseMessage ||
      raw.message ||
      inner.error ||
      '';
    const referenceId = inner.referenceId || raw.referenceId || '';

    // Profile nested under data.data
    if (
      inner.data &&
      typeof inner.data === 'object' &&
      (inner.data.name ||
        inner.data.documentType ||
        inner.data.firstName ||
        inner.data.fullName ||
        inner.data.status ||
        inner.data.pan)
    ) {
      return {
        responseCode,
        responseMessage,
        referenceId,
        data: inner.data,
        success: raw.success,
        status: raw.status,
      };
    }

    // Profile fields mixed with response meta on same object
    if (
      inner.name ||
      inner.documentType ||
      inner.dateOfBirth ||
      inner.firstName ||
      inner.fullName ||
      inner.pan ||
      String(inner.status || '').toUpperCase() === 'VALID'
    ) {
      const {
        responseCode: _rc,
        responseMessage: _rm,
        referenceId: _ri,
        message: _msg,
        error: _err,
        ...profile
      } = inner;
      return {
        responseCode,
        responseMessage,
        referenceId,
        data: Object.keys(profile).length ? profile : null,
        success: raw.success,
        status: raw.status,
      };
    }

    // Error-only wrapper (e.g. Credits not available)
    return {
      responseCode: responseCode || String(inner.status || raw.status_code || ''),
      responseMessage,
      referenceId,
      data: null,
      success: raw.success,
      status: raw.success === false ? false : raw.status,
    };
  }

  return {
    responseCode: String(raw.responseCode || raw.status_code || ''),
    responseMessage: raw.responseMessage || raw.message || raw.error || '',
    referenceId: raw.referenceId || '',
    data: null,
    success: raw.success,
    status: raw.status,
    failed: raw.failed,
  };
};

const getAadhaarApiFeedback = (data, fallbackMessage) => ({
  code: data?.responseCode || data?.status_code || data?.status || '',
  message:
    data?.responseMessage ||
    data?.message ||
    data?.error ||
    data?.data?.responseMessage ||
    data?.data?.message ||
    fallbackMessage,
});

/** @deprecated use normalizeKycApiResponse */
const normalizeAadhaarApiResponse = normalizeKycApiResponse;

const validatePan = (pan) => {
  const value = pan.trim();
  if (!value) return 'PAN is required';
  if (value.length < 10) return 'PAN must be 10 characters';
  if (!PAN_REGEX.test(value)) return 'Enter a valid PAN (e.g. ABCDE1234F)';
  return '';
};

const validateAadhaar = (aadhaar) => {
  const value = aadhaar.trim();
  if (!value) return 'Aadhaar is required';
  if (!AADHAAR_REGEX.test(value)) return 'Enter a valid 12-digit Aadhaar number';
  if (/^(\d)\1{11}$/.test(value)) return 'Enter a valid Aadhaar number';
  return '';
};

const validateOtp = (otp) => {
  if (!otp) return 'OTP is required';
  if (otp.length !== 6) return 'Enter 6-digit OTP';
  if (!ONLY_DIGITS.test(otp)) return 'OTP must contain digits only';
  return '';
};

const parseFullName = (fullName) => {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', middleName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], middleName: '', lastName: '' };
  if (parts.length === 2) return { firstName: parts[0], middleName: '', lastName: parts[1] };
  return {
    firstName: parts[0],
    middleName: parts.slice(1, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
};

const formatHiddenFullName = (profile) =>
  [profile?.firstName, profile?.middleName, profile?.lastName]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');

const mapGender = (gender) => {
  const value = String(gender || '').toLowerCase();
  if (value.startsWith('m')) return 'male';
  if (value.startsWith('f')) return 'female';
  return value;
};

const extractAadhaarLastFour = (...values) => {
  for (const value of values) {
    if (!value) continue;
    const digits = String(value).replace(/\D/g, '');
    if (digits.length >= 4) return digits.slice(-4);
  }
  return '';
};

const buildAadhaarAddress = (data) => {
  const parts = [
    data.careOf || data.care_of,
    data.house,
    data.street,
    data.landmark,
    data.locality,
    data.vtcName || data.vtc,
    data.subDistrict || data.sub_district,
    data.district || data.dist,
    data.postOfficeName || data.post_office,
    data.state,
    data.pincode || data.pc,
    data.country,
  ]
    .map((part) => (part || '').trim())
    .filter(Boolean);
  return parts.join(', ');
};

const normalizeDigitapDob = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  }
  return raw;
};

const mapTaskApiDigitapProfile = (data) => {
  const row = data?.Data || data?.data;
  if (!row || typeof row !== 'object') return null;

  const customerName = row.customer_name || row.name || '';
  const maskedAadhaar =
    row.masked_aadhaar_number || row.masked_aadhaar || row.maskedAadhaarNumber || '';
  if (!customerName && !maskedAadhaar && !row.dob && !row.gender) return null;

  return {
    name: customerName,
    gender: row.gender || '',
    dob: normalizeDigitapDob(row.dob),
    dateOfBirth: normalizeDigitapDob(row.dob),
    maskAadhaarNumber: maskedAadhaar,
    maskedAadhaarNumber: maskedAadhaar,
    careOf: row.care_of || '',
    care_of: row.care_of || '',
    house: row.house || '',
    street: row.street || '',
    landmark: row.landmark || '',
    locality: row.locality || '',
    vtc: row.vtc || row.village || '',
    sub_district: row.sub_district || '',
    district: row.district || '',
    post_office: row.post_office || '',
    state: row.state || '',
    pincode: row.pincode || '',
    country: row.country || '',
    mobile: row.mobile || '',
    email: row.email || '',
  };
};

const REAPPLY_ALLOWED_LOAN_STATUSES = ['FRESH', 'NEW', 'CLOSED', 'CLOSE', 'REJECT', 'REJECTED'];

const isLeadStatusAllowedToApply = (loanStatus) => {
  if (!loanStatus) return true;
  return REAPPLY_ALLOWED_LOAN_STATUSES.includes(String(loanStatus).toUpperCase());
};

const getLeadStatusDecision = (customerData) => {
  const loanStatus = customerData?.status
    ? String(customerData.status).toUpperCase()
    : '';

  if (!loanStatus) {
    return {
      allowed: true,
      feedback: { type: 'success', message: 'Customer details loaded successfully.' },
    };
  }

  if (isLeadStatusAllowedToApply(loanStatus)) {
    return {
      allowed: true,
      feedback: {
        type: 'success',
        message: `Latest Status: ${loanStatus} (You can re-apply)`,
      },
    };
  }

  return {
    allowed: false,
    feedback: {
      type: 'error',
      message: `Your loan status is ${loanStatus}. Cannot re-apply.`,
    },
  };
};

const PAN_NOT_FOUND_MESSAGE = 'PAN does not exist. Enter your correct PAN.';
const CROSS_VERTICAL_DISBURSED_BLOCK_MESSAGE =
  'You have already taken a loan from another NBFC. A new application cannot be submitted at this time.';

/** Count group verticals that already have at least one DISBURSED loan for this PAN */
const countDisbursedVerticals = (apiData) => {
  const rows = Array.isArray(apiData?.data) ? apiData.data : [];
  let count = 0;
  for (const vertical of rows) {
    const records = Array.isArray(vertical?.records) ? vertical.records : [];
    const hasDisbursed = records.some(
      (record) => String(record?.lead_status || '').toUpperCase() === 'DISBURSED'
    );
    if (hasDisbursed) count += 1;
  }
  return count;
};

/** 2+ verticals already DISBURSED → block 3rd apply */
const isCrossVerticalDisbursedBlocked = (apiData) => countDisbursedVerticals(apiData) >= 2;

const getCrossVerticalApiMessage = (data, err, fallback) => {
  const fromData =
    data?.message ||
    data?.Message ||
    data?.error ||
    data?.responseMessage ||
    (typeof data === 'string' ? data : '');
  if (fromData) return formatApiMessage(String(fromData));

  const errData = err?.response?.data;
  if (typeof errData === 'string' && errData.trim()) return formatApiMessage(errData);
  if (errData && typeof errData === 'object') {
    const nested =
      errData.message || errData.Message || errData.error || errData.responseMessage || '';
    if (nested) return formatApiMessage(String(nested));
  }

  if (err?.message) return formatApiMessage(err.message);
  return fallback;
};

const isPanNotFoundResponse = (data) => {
  if (!data || data.failed) return false;

  const code = String(data.responseCode || '').toUpperCase();
  const message = String(data.responseMessage || data.message || '').toLowerCase();

  return code === 'ENI004' || message.includes('no information found');
};

const isVerifyPanSuccess = (verifyPanResult) => {
  if (isPanNotFoundResponse(verifyPanResult)) return false;

  if (
    !verifyPanResult ||
    verifyPanResult.failed ||
    verifyPanResult.status === false ||
    verifyPanResult.success === false
  ) {
    return false;
  }

  if (verifyPanResult.responseCode !== 'SRC001' || !verifyPanResult.data) {
    return false;
  }

  const panStatus = String(verifyPanResult.data.status || '').toUpperCase();
  if (panStatus && panStatus !== 'VALID') {
    return false;
  }

  return true;
};

/** When govt PAN API fails (406 / credits / etc.) — do not show misleading "customer not found". */
const getPanVerifyFailureFeedback = (verifyPanResult) => {
  if (!verifyPanResult || verifyPanResult.failed || isVerifyPanSuccess(verifyPanResult)) {
    return null;
  }

  const msg = String(
    verifyPanResult.responseMessage || verifyPanResult.message || verifyPanResult.error || ''
  ).trim();

  if (!msg && verifyPanResult.success !== false && !verifyPanResult.responseCode) {
    return null;
  }

  return {
    type: 'warn',
    message: msg
      ? `PAN verification failed: ${msg}. You may continue with Aadhaar OTP.`
      : 'PAN online verification failed. You may continue with Aadhaar OTP.',
  };
};

const buildPanPayloadFields = (verifyPanResult) => {
  const emptyFields = {
    pan_responseCode: '',
    pan_responseMessage: '',
    pan_referenceId: '',
    pan_firstName: '',
    pan_lastName: '',
    pan_address: '',
    pan_gender: '',
    pan_aadhaarLinked: '',
    pan_dob: '',
    pan_fullName: '',
    pan_maskedAadhaarNumber: '',
    pan_category: '',
    pan_verifiedPan: '',
  };

  if (!verifyPanResult || verifyPanResult.failed || !isVerifyPanSuccess(verifyPanResult)) {
    // Still pass through raw codes/messages if present (failed verify / credits error)
    if (verifyPanResult && !verifyPanResult.failed) {
      return {
        ...emptyFields,
        pan_responseCode: verifyPanResult.responseCode || '',
        pan_responseMessage: verifyPanResult.responseMessage || verifyPanResult.message || '',
        pan_referenceId: verifyPanResult.referenceId || '',
      };
    }
    return emptyFields;
  }

  const data = verifyPanResult.data || {};

  return {
    pan_responseCode: verifyPanResult.responseCode || '',
    pan_responseMessage: verifyPanResult.responseMessage || '',
    pan_referenceId: verifyPanResult.referenceId || '',
    pan_firstName: data.firstName || data.first_name || '',
    pan_lastName: data.lastName || data.last_name || '',
    pan_address: data.address || '',
    pan_gender: data.gender || '',
    pan_aadhaarLinked:
      data.aadhaarLinked === true || data.aadhaarLinked === 'Y' || data.aadhaarLinked === 'true'
        ? 'Y'
        : data.aadhaarLinked === false || data.aadhaarLinked === 'N'
          ? 'N'
          : data.aadhaarLinked || '',
    pan_dob: data.dob || data.dateOfBirth || data.date_of_birth || '',
    pan_fullName: data.fullName || data.full_name || data.name || '',
    pan_maskedAadhaarNumber:
      data.maskedAadhaarNumber || data.maskAadhaarNumber || data.masked_aadhaar || '',
    pan_category: data.category || '',
    pan_verifiedPan: data.pan || data.verifiedPan || '',
  };
};

const isLoanBlockedResponse = (data) => {
  const msg = String(data?.message || '').toLowerCase();
  return msg.includes('cannot re-apply') || msg.includes('loan status is');
};

const isCustomerNotFoundResponse = (data) => {
  if (!data || data.status === true || isLoanBlockedResponse(data)) {
    return false;
  }

  const msg = String(data.message || '').trim().toLowerCase();

  return (
    msg.includes('not found') ||
    msg.includes('no customer') ||
    msg.includes('pan not found in any database')
  );
};

const validateMobile = (mobile, { required = true, label = 'Mobile number' } = {}) => {
  const value = mobile.trim();
  if (!value) return required ? `${label} is required` : '';
  if (!MOBILE_REGEX.test(value)) return 'Enter a valid 10-digit mobile (starts with 6-9)';
  return '';
};

const validateEmail = (email, { required = true, label = 'Email' } = {}) => {
  const value = email.trim();
  if (!value) return required ? `${label} is required` : '';
  if (value.length > MAX_EMAIL_LENGTH) return `${label} is too long`;
  if (!EMAIL_REGEX.test(value)) return 'Enter a valid email address';
  return '';
};

const validateAmount = (
  value,
  { required = true, label, min, max, allowZero = false }
) => {
  const trimmed = String(value).trim();
  if (!trimmed) return required ? `${label} is required` : '';

  if (!ONLY_DIGITS.test(trimmed)) return `${label} must be a whole number`;
  const amount = Number(trimmed);
  if (!allowZero && amount <= 0) return `${label} must be greater than 0`;
  if (min !== undefined && amount < min) {
    return `${label} must be at least \u20B9${min.toLocaleString('en-IN')}`;
  }
  if (max !== undefined && amount > max) {
    return `${label} must not exceed \u20B9${max.toLocaleString('en-IN')}`;
  }
  return '';
};

const validatePincode = (pincode) => {
  const value = String(pincode).trim();
  if (!value) return 'Pincode is required';
  if (!PINCODE_REGEX.test(value)) return 'Enter a valid 6-digit pincode';
  return '';
};

const validateAddress = (address) => {
  const value = address.trim();
  if (!value) return 'Address is required';
  if (value.length < MIN_ADDRESS_LENGTH) {
    return `Address must be at least ${MIN_ADDRESS_LENGTH} characters`;
  }
  if (value.length > MAX_ADDRESS_LENGTH) {
    return `Address must not exceed ${MAX_ADDRESS_LENGTH} characters`;
  }
  if (!/[a-zA-Z0-9]/.test(value)) return 'Enter a valid address';
  return '';
};

const initialHiddenProfile = {
  firstName: '',
  middleName: '',
  lastName: '',
  dob: '',
  gender: '',
  aadhaarLastFour: '',
};

const initialStep1 = {
  panNumber: '',
  aadhaarNumber: '',
  otp: '',
};

const initialStep2 = {
  monthlyIncome: '',
  loanAmount: '',
  mobile: '',
  altMobile: '',
  email: '',
  officialEmail: '',
  obligations: '',
  state: '',
  city: '',
  pincode: '',
  address: '',
  termsAccepted: false,
};

const getRemainingOtpSeconds = (sentAt, totalSeconds = OTP_RESEND_SECONDS) => {
  if (!sentAt) return 0;
  const elapsed = Math.floor((Date.now() - Number(sentAt)) / 1000);
  if (!Number.isFinite(elapsed) || elapsed < 0) return totalSeconds;
  return Math.max(0, totalSeconds - elapsed);
};

const getInitialApplyLoanState = () => {
  const callback =
    typeof window !== 'undefined'
      ? resolveDigitapCallbackTxn(getBrowserSearch())
      : { txnId: '', aadhaarVerified: false, failed: false };
  const callbackTxnId =
    callback.aadhaarVerified &&
    !callback.failed &&
    isValidDigitapTransactionId(callback.txnId)
      ? normalizeDigitapTransactionId(callback.txnId)
      : '';

  const draft = typeof window !== 'undefined' ? readApplyLoanDraft() : null;
  if (!draft) {
    const callbackBoot = Boolean(callbackTxnId);
    return {
      step1: initialStep1,
      step2: initialStep2,
      hiddenProfile: initialHiddenProfile,
      mobileVerified: callbackBoot,
      mobileOtpSent: false,
      mobileOtpSentAt: 0,
      mobileOtpCountdown: 0,
      otpVerified: false,
      digitapPending: callbackBoot,
      digitapTransactionId: callbackTxnId,
      digitapCallbackBoot: callbackBoot,
      digitapReturnConfirmed: callbackBoot,
      stateId: '',
      cityId: '',
      panVerifyStatus: 0,
      panResponseData: null,
      panVerified: false,
      panLeadBlocked: false,
      panStatusFeedback: null,
      lastVerifiedPan: '',
    };
  }

  let mobileVerified = Boolean(draft.mobileVerified) || Boolean(draft.otpVerified);
  const otpVerified = Boolean(draft.otpVerified);
  let digitapPending =
    mobileVerified && !otpVerified && Boolean(draft.digitapPending) && Boolean(draft.digitapTransactionId);
  let digitapTransactionId =
    digitapPending || draft.digitapTransactionId ? String(draft.digitapTransactionId || '') : '';
  let digitapCallbackBoot = false;

  if (callbackTxnId && !otpVerified) {
    mobileVerified = true;
    digitapPending = true;
    digitapTransactionId = callbackTxnId;
    digitapCallbackBoot = true;
  }

  const digitapReturnConfirmed = Boolean(callbackTxnId && !otpVerified);

  const mobileOtpSent =
    !mobileVerified && Boolean(draft.mobileOtpSent) && Boolean(draft.step2?.mobile);
  const mobileOtpSentAt = mobileOtpSent ? Number(draft.mobileOtpSentAt) || 0 : 0;
  const mobileOtpCountdown = mobileOtpSent
    ? getRemainingOtpSeconds(mobileOtpSentAt || draft.savedAt)
    : 0;

  return {
    step1: {
      ...initialStep1,
      ...(draft.step1 || {}),
      otp: '', // never restore OTP codes
    },
    step2: {
      ...initialStep2,
      ...(draft.step2 || {}),
      state: draft.step2?.state || '',
      city: draft.step2?.city || '',
      pincode: draft.step2?.pincode || '',
      termsAccepted: Boolean(draft.step2?.termsAccepted),
    },
    hiddenProfile: {
      ...initialHiddenProfile,
      ...(draft.hiddenProfile || {}),
    },
    mobileVerified,
    mobileOtpSent,
    mobileOtpSentAt,
    mobileOtpCountdown,
    otpVerified,
    digitapPending,
    digitapTransactionId,
    digitapCallbackBoot,
    digitapReturnConfirmed,
    stateId: draft.stateId ? String(draft.stateId) : '',
    cityId: draft.cityId ? String(draft.cityId) : '',
    panVerifyStatus: draft.panVerifyStatus === 1 ? 1 : 0,
    panResponseData: draft.panResponseData || null,
    panVerified: Boolean(draft.panVerified),
    panLeadBlocked: Boolean(draft.panLeadBlocked),
    panStatusFeedback: draft.panStatusFeedback || null,
    lastVerifiedPan: draft.lastVerifiedPan || '',
  };
};

const mapCustomerToHiddenProfile = (data) => ({
  firstName: data.first_name || '',
  middleName: data.middle_name || '',
  lastName: data.sur_name || '',
  dob: data.dob || '',
  gender: data.gender ? String(data.gender).toLowerCase() : '',
});

const CustomDropdown = ({
  id,
  label,
  required,
  icon,
  value,
  options,
  placeholder,
  disabled,
  onChange,
  error,
  searchable = true,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(search.trim().toLowerCase())
  );

  const selectedOption = options.find((option) => option.value === value);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setOpen(false);
    setSearch('');
  };

  return (
    <div
      ref={dropdownRef}
      className={`apply-mrrupya-field apply-mrrupya-dropdown-field ${error ? 'has-error' : ''} ${disabled ? 'is-disabled' : ''}`}
    >
      <label htmlFor={id}>
        {label} {required && <span>*</span>}
      </label>

      <button
        type="button"
        id={id}
        className={`apply-mrrupya-dropdown-trigger ${open ? 'is-open' : ''} ${selectedOption ? 'has-value' : ''}`}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        disabled={disabled}
      >
        <span className="apply-mrrupya-dropdown-trigger-icon">{icon}</span>
        <span className={`apply-mrrupya-dropdown-value ${selectedOption ? '' : 'is-placeholder'}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className="apply-mrrupya-dropdown-chevron">
          <FiChevronDown />
        </span>
      </button>

      {open && !disabled && (
        <div className="apply-mrrupya-dropdown-menu">
          {searchable && options.length > 6 && (
            <div className="apply-mrrupya-dropdown-search">
              <FiSearch />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}...`}
                autoFocus
              />
            </div>
          )}

          <ul className="apply-mrrupya-dropdown-list">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li key={option.value}>
                  <button
                    type="button"
                    className={`apply-mrrupya-dropdown-option ${option.value === value ? 'is-selected' : ''}`}
                    onClick={() => handleSelect(option.value)}
                  >
                    <span>{option.label}</span>
                    {option.value === value && <FiCheckCircle />}
                  </button>
                </li>
              ))
            ) : (
              <li className="apply-mrrupya-dropdown-empty">No results found</li>
            )}
          </ul>
        </div>
      )}

      {error && <span className="apply-mrrupya-error">{error}</span>}
    </div>
  );
};

const ApplyLoan = () => {
  const location = useLocation();
  const [boot] = useState(getInitialApplyLoanState);

  const [step1, setStep1] = useState(boot.step1);
  const [step2, setStep2] = useState(boot.step2);
  const [hiddenProfile, setHiddenProfile] = useState(boot.hiddenProfile);
  const [errors, setErrors] = useState({});
  const [mobileVerified, setMobileVerified] = useState(() => {
    if (boot.mobileVerified) return true;
    if (typeof window !== 'undefined') {
      const cb = resolveDigitapCallbackTxn(getBrowserSearch());
      return Boolean(cb.txnId && cb.aadhaarVerified && !cb.failed);
    }
    return false;
  });
  const [mobileOtpSent, setMobileOtpSent] = useState(boot.mobileOtpSent);
  const [mobileOtpSentAt, setMobileOtpSentAt] = useState(boot.mobileOtpSentAt || 0);
  const [mobileOtp, setMobileOtp] = useState('');
  const [mobileOtpMessage, setMobileOtpMessage] = useState(
    boot.mobileOtpSent && !boot.mobileVerified
      ? {
          type: 'info',
          message: `OTP was already sent to ${maskMobileNumber(boot.step2?.mobile || '')}. Enter OTP to continue.`,
        }
      : null
  );
  const [mobileOtpCountdown, setMobileOtpCountdown] = useState(boot.mobileOtpCountdown || 0);
  const [sendingMobileOtp, setSendingMobileOtp] = useState(false);
  const [verifyingMobileOtp, setVerifyingMobileOtp] = useState(false);
  const [otpVerified, setOtpVerified] = useState(boot.otpVerified);
  const [otpApiMessage, setOtpApiMessage] = useState(null);
  const digitapPollRef = useRef(null);
  const digitapPollStartedRef = useRef(0);
  const digitapWindowRef = useRef(null);
  const digitapWindowWatchRef = useRef(null);
  const otpVerifiedRef = useRef(false);
  const [digitapVerifying, setDigitapVerifying] = useState(false);
  const [digitapTransactionId, setDigitapTransactionId] = useState(() => {
    if (boot.digitapTransactionId) return boot.digitapTransactionId;
    if (typeof window !== 'undefined') {
      const cb = resolveDigitapCallbackTxn(getBrowserSearch());
      if (cb.txnId && cb.aadhaarVerified && !cb.failed) {
        return normalizeDigitapTransactionId(cb.txnId);
      }
    }
    return '';
  });
  const [digitapKycUrl, setDigitapKycUrl] = useState('');
  const [digitapPending, setDigitapPending] = useState(() => {
    if (boot.digitapPending) return true;
    if (typeof window !== 'undefined') {
      const cb = resolveDigitapCallbackTxn(getBrowserSearch());
      return Boolean(cb.txnId && cb.aadhaarVerified && !cb.failed && !boot.otpVerified);
    }
    return false;
  });
  const [digitapReturnConfirmed, setDigitapReturnConfirmed] = useState(() => {
    if (boot.digitapReturnConfirmed) return true;
    if (typeof window !== 'undefined') {
      const cb = resolveDigitapCallbackTxn(getBrowserSearch());
      return Boolean(cb.txnId && cb.aadhaarVerified && !cb.failed && !boot.otpVerified);
    }
    return false;
  });
  const digitapPollAttemptsRef = useRef(0);
  const digitapCallbackInitRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedSummary, setSubmittedSummary] = useState(null);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [stateId, setStateId] = useState(boot.stateId);
  const [cityId, setCityId] = useState(boot.cityId);
  const [pincodeList, setPincodeList] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [citiesLoadError, setCitiesLoadError] = useState('');
  const [loadingPincodes, setLoadingPincodes] = useState(false);
  const citiesRequestRef = useRef(0);
  const pincodesRequestRef = useRef(0);
  const lastVerifiedPanRef = useRef(boot.lastVerifiedPan || '');
  const submittingLockRef = useRef(false);
  const mobileOtpLockRef = useRef(false);
  const [panChecking, setPanChecking] = useState(false);
  const [panStatusFeedback, setPanStatusFeedback] = useState(boot.panStatusFeedback);
  const [panVerifyStatus, setPanVerifyStatus] = useState(boot.panVerifyStatus);
  const [panResponseData, setPanResponseData] = useState(boot.panResponseData);
  const [panVerified, setPanVerified] = useState(boot.panVerified);
  const [panLeadBlocked, setPanLeadBlocked] = useState(boot.panLeadBlocked);
  const [statesLoadError, setStatesLoadError] = useState('');
  const [showResumeNote] = useState(
    () =>
      Boolean(
        boot.mobileVerified ||
          boot.mobileOtpSent ||
          boot.otpVerified ||
          boot.digitapPending ||
          boot.step1?.panNumber ||
          boot.step2?.mobile
      )
  );
  useEffect(() => {
    if (submitted) return;

    const liveCallback = resolveDigitapCallbackTxn(getBrowserSearch() || location.search);
    if (
      liveCallback.txnId &&
      liveCallback.aadhaarVerified &&
      !liveCallback.failed &&
      !otpVerified
    ) {
      return;
    }

    writeApplyLoanDraft({
      step1: {
        panNumber: step1.panNumber,
        aadhaarNumber: step1.aadhaarNumber,
        otp: '',
      },
      step2,
      hiddenProfile,
      mobileVerified,
      mobileOtpSent: mobileVerified ? false : mobileOtpSent,
      mobileOtpSentAt: mobileVerified ? 0 : mobileOtpSentAt,
      otpVerified,
      digitapPending: otpVerified ? false : digitapPending,
      digitapTransactionId: otpVerified ? '' : digitapTransactionId,
      stateId,
      cityId,
      panVerifyStatus,
      panResponseData,
      panVerified,
      panLeadBlocked,
      panStatusFeedback,
      lastVerifiedPan: lastVerifiedPanRef.current || '',
    });
  }, [
    step1.panNumber,
    step1.aadhaarNumber,
    step2,
    hiddenProfile,
    mobileVerified,
    mobileOtpSent,
    mobileOtpSentAt,
    otpVerified,
    digitapPending,
    digitapTransactionId,
    stateId,
    cityId,
    panVerifyStatus,
    panResponseData,
    panVerified,
    panLeadBlocked,
    panStatusFeedback,
    submitted,
  ]);

  useEffect(() => {
    axios
      .get(taskApiPostUrl('getState'))
      .then((res) => {
        if (res.data.Status === 1) {
          setStates(res.data.Data);
          setStatesLoadError('');
        } else {
          setStatesLoadError(res.data.Message || 'Failed to fetch states');
        }
      })
      .catch((err) => {
        console.error('Failed fetching states:', err);
        setStatesLoadError('Could not load states. Please refresh the page.');
      });
  }, []);

  useEffect(() => {
    if (!mobileOtpSent || mobileOtpCountdown <= 0) return undefined;

    const timer = window.setInterval(() => {
      setMobileOtpCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [mobileOtpSent, mobileOtpCountdown]);

  useEffect(() => {
    otpVerifiedRef.current = otpVerified;
  }, [otpVerified]);

  useEffect(
    () => () => {
      stopDigitapPolling();
      stopDigitapWindowWatch();
    },
    []
  );

  useEffect(() => {
    if (boot.digitapCallbackBoot) return undefined;
    if (!digitapPending || !digitapTransactionId || otpVerified) return undefined;
    setDigitapVerifying(true);
    pollDigitapKycStatus(digitapTransactionId);
    return () => stopDigitapPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFieldError = (field, message) => {
    setErrors((prev) => ({ ...prev, [field]: message }));
  };

  const clearFieldError = (field) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const formatAadhaarRaw = (value) => value.replace(/\D/g, '').slice(0, 12);

  const handleStep1Change = (field, value) => {
    let nextValue = value;

    if (field === 'panNumber') {
      nextValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);

      if (nextValue !== lastVerifiedPanRef.current) {
        setHiddenProfile(initialHiddenProfile);
        setPanStatusFeedback(null);
        setPanVerifyStatus(0);
        setPanResponseData(null);
        setPanLeadBlocked(false);
        setPanVerified(false);
        setStateId('');
        setCityId('');
        setCities([]);
        setPincodeList([]);
        setStep2((prev) => ({
          ...prev,
          state: '',
          city: '',
          pincode: '',
        }));
      }
    } else if (field === 'aadhaarNumber') {
      nextValue = formatAadhaarRaw(value);
    }

    setStep1((prev) => ({ ...prev, [field]: nextValue }));

    if (field === 'panNumber' && !otpVerified && digitapPending) {
      stopDigitapPolling();
      stopDigitapWindowWatch();
      setDigitapPending(false);
      setDigitapVerifying(false);
      setDigitapTransactionId('');
      setDigitapKycUrl('');
      setOtpApiMessage({
        type: 'info',
        message: 'PAN updated. Please continue again to verify with DigiLocker.',
      });
    }

    clearFieldError(field);

    if (field === 'panNumber' && nextValue.length === 10) {
      checkPan(nextValue);
    }
  };

  const handleStep2Change = (e) => {
    const { id, value, type, checked } = e.target;
    let nextValue = value;

    if (id === 'mobile' && mobileVerified) return;

    if (id === 'mobile' || id === 'altMobile') {
      nextValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (id === 'monthlyIncome' || id === 'loanAmount' || id === 'obligations') {
      nextValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (id === 'email' || id === 'officialEmail') {
      nextValue = value.slice(0, MAX_EMAIL_LENGTH);
    } else if (id === 'address') {
      nextValue = value.slice(0, MAX_ADDRESS_LENGTH);
    }

    setStep2((prev) => ({
      ...prev,
      [id]: type === 'checkbox' ? checked : nextValue,
    }));
    clearFieldError(id);

    if (id === 'mobile' && !mobileVerified) {
      setMobileOtpSent(false);
      setMobileOtpSentAt(0);
      setMobileOtp('');
      setMobileOtpMessage(null);
      setMobileOtpCountdown(0);
      clearFieldError('mobileOtp');
    }
  };

  const handleMobileOtpChange = (value) => {
    const nextValue = value.replace(/\D/g, '').slice(0, 6);
    setMobileOtp(nextValue);
    setMobileOtpMessage(null);
    clearFieldError('mobileOtp');
  };

  const handleSendMobileOtp = async (isResend = false) => {
    if (mobileOtpLockRef.current || sendingMobileOtp || verifyingMobileOtp || mobileVerified) {
      return;
    }

    const mobileError = validateMobile(step2.mobile);
    if (mobileError) {
      setFieldError('mobile', mobileError);
      return;
    }

    if (!isResend && mobileOtpCountdown > 0) {
      setMobileOtpMessage({
        type: 'warn',
        message: `Please wait ${formatCountdown(mobileOtpCountdown)} before requesting another OTP.`,
      });
      return;
    }

    mobileOtpLockRef.current = true;
    setSendingMobileOtp(true);
    clearFieldError('mobile');
    clearFieldError('mobileOtp');
    setMobileOtpMessage(null);

    try {
      const resp = await axios.post(mobileOtpUrl('send'), buildMobileOtpBody('send', step2.mobile.trim()), {
        headers: mobileOtpRequestHeaders(),
        timeout: 30000,
      });

      if (isMobileOtpSendSuccess(resp.data)) {
        const sentAt = Date.now();
        setMobileOtpSent(true);
        setMobileOtpSentAt(sentAt);
        setMobileOtp('');
        setMobileOtpCountdown(OTP_RESEND_SECONDS);
        setMobileOtpMessage({
          type: 'success',
          message: getMobileOtpMessage(resp.data, 'OTP sent successfully.'),
        });
        return;
      }

      const message = getMobileOtpMessage(resp.data, 'Failed to send OTP. Please try again.');
      setMobileOtpMessage({ type: 'error', message });
      setFieldError('mobile', message);
    } catch (err) {
      const message = getMobileOtpMessage(
        err.response?.data,
        err.message || 'Failed to send OTP. Please try again.'
      );
      setMobileOtpMessage({ type: 'error', message });
      setFieldError('mobile', message);
    } finally {
      setSendingMobileOtp(false);
      mobileOtpLockRef.current = false;
    }
  };

  const handleVerifyMobileOtp = async () => {
    if (mobileOtpLockRef.current || verifyingMobileOtp || sendingMobileOtp || mobileVerified) {
      return;
    }

    const mobileError = validateMobile(step2.mobile);
    if (mobileError) {
      setFieldError('mobile', mobileError);
      return;
    }

    const otpError = validateOtp(mobileOtp);
    if (otpError) {
      setFieldError('mobileOtp', otpError);
      return;
    }

    mobileOtpLockRef.current = true;
    setVerifyingMobileOtp(true);
    setMobileOtpMessage(null);

    try {
      const resp = await axios.post(
        mobileOtpUrl('verify'),
        buildMobileOtpBody('verify', step2.mobile.trim(), mobileOtp.trim()),
        {
        headers: mobileOtpRequestHeaders(),
        timeout: 30000,
      });

      if (isMobileOtpVerifySuccess(resp.data)) {
        setMobileVerified(true);
        setMobileOtpSent(false);
        setMobileOtpSentAt(0);
        setMobileOtp(''); // clear OTP from memory after success
        setMobileOtpCountdown(0);
        setMobileOtpMessage({
          type: 'success',
          message: getMobileOtpMessage(resp.data, 'Mobile verified successfully.'),
        });
        clearFieldError('mobile');
        clearFieldError('mobileOtp');
        return;
      }

      const message = getMobileOtpMessage(resp.data, 'Invalid OTP. Please try again.');
      setMobileOtpMessage({ type: 'error', message });
      setFieldError('mobileOtp', message);
      setMobileOtp(''); // clear wrong OTP
    } catch (err) {
      const message = getMobileOtpMessage(
        err.response?.data,
        err.message || 'OTP verification failed. Please try again.'
      );
      setMobileOtpMessage({ type: 'error', message });
      setFieldError('mobileOtp', message);
      setMobileOtp('');
    } finally {
      setVerifyingMobileOtp(false);
      mobileOtpLockRef.current = false;
    }
  };

  const validateStep1Field = (field) => {
    let message = '';

    if (field === 'panNumber') message = validatePan(step1.panNumber);

    if (message) setFieldError(field, message);
    else clearFieldError(field);
    return !message;
  };

  const validateStep2Field = (field) => {
    let message = '';

    switch (field) {
      case 'monthlyIncome':
        message = validateAmount(step2.monthlyIncome, {
          label: 'Monthly income',
          min: MIN_MONTHLY_INCOME,
          max: MAX_MONTHLY_INCOME,
        });
        break;
      case 'loanAmount':
        message = validateAmount(step2.loanAmount, {
          label: 'Loan amount',
          min: MIN_LOAN_AMOUNT,
          max: MAX_LOAN_AMOUNT,
        });
        if (!message && step2.monthlyIncome) {
          const income = Number(step2.monthlyIncome);
          const loan = Number(step2.loanAmount);
          if (ONLY_DIGITS.test(step2.monthlyIncome) && loan > income * 5) {
            message = 'Loan amount cannot exceed 5Ã— your monthly income';
          }
        }
        break;
      case 'mobile':
        message = validateMobile(step2.mobile);
        break;
      case 'altMobile':
        message = validateMobile(step2.altMobile, { required: false, label: 'Alternate mobile' });
        if (!message && step2.altMobile && step2.altMobile === step2.mobile) {
          message = 'Alternate mobile must be different from mobile';
        }
        break;
      case 'email':
        message = validateEmail(step2.email);
        break;
      case 'officialEmail':
        message = validateEmail(step2.officialEmail, {
          required: false,
          label: 'Official email',
        });
        if (
          !message &&
          step2.officialEmail.trim() &&
          step2.email.trim() &&
          step2.officialEmail.trim().toLowerCase() === step2.email.trim().toLowerCase()
        ) {
          message = 'Official email must be different from personal email';
        }
        break;
      case 'obligations':
        if (step2.obligations) {
          message = validateAmount(step2.obligations, {
            required: false,
            label: 'Obligations',
            min: 0,
            max: MAX_OBLIGATIONS,
            allowZero: true,
          });
          if (
            !message &&
            step2.monthlyIncome &&
            ONLY_DIGITS.test(step2.monthlyIncome) &&
            Number(step2.obligations) > Number(step2.monthlyIncome)
          ) {
            message = 'Obligations cannot exceed monthly income';
          }
        }
        break;
      case 'state':
        if (!stateId || !step2.state) message = 'State is required';
        break;
      case 'city':
        if (!cityId || !step2.city) message = 'City is required';
        break;
      case 'pincode':
        message = validatePincode(step2.pincode);
        if (!message && cityId && pincodeList.length > 0) {
          const pinExists = pincodeList.some(
            (pin) => String(pin.m_pincode_value) === String(step2.pincode)
          );
          if (!pinExists) message = 'Please select a valid pincode';
        } else if (!message && cityId && pincodeList.length === 0) {
          message = 'No pincodes available for selected city';
        }
        break;
      case 'address':
        message = validateAddress(step2.address);
        break;
      case 'termsAccepted':
        if (!step2.termsAccepted) message = 'Please accept terms & conditions';
        break;
      default:
        break;
    }

    if (message) setFieldError(field, message);
    else clearFieldError(field);
    return !message;
  };

  const handleStep1Blur = (field) => {
    validateStep1Field(field);
    if (field === 'panNumber' && step1.panNumber.trim().length === 10) {
      checkPan(step1.panNumber);
    }
  };

  const handleStep2Blur = (field) => {
    validateStep2Field(field);
  };

  const validateIdentityFields = () => {
    const identityFields = ['panNumber'];
    const nextErrors = {};

    const panError = validatePan(step1.panNumber);
    if (panError) nextErrors.panNumber = panError;

    if (!panError && !panVerified) {
      nextErrors.panNumber = 'Please wait until PAN is verified.';
    }

    if (panLeadBlocked) {
      nextErrors.panNumber =
        errors.panNumber || panStatusFeedback?.message || 'Your loan is in progress. Cannot re-apply.';
    }

    setErrors((prev) => {
      const updated = { ...prev };
      identityFields.forEach((field) => delete updated[field]);
      return { ...updated, ...nextErrors };
    });
    return Object.keys(nextErrors).length === 0;
  };

  const blockPanLead = (pan, message, verifyPanSuccess = false) => {
    lastVerifiedPanRef.current = pan;
    setPanLeadBlocked(true);
    setPanStatusFeedback({ type: 'error', message });
    setFieldError('panNumber', message);
    return {
      blocked: true,
      checked: true,
      panVerifyStatus: verifyPanSuccess ? 1 : 0,
    };
  };

  const completePanCheck = (pan, feedback, verified = 0) => {
    const normalizedStatus = verified === 1 ? 1 : 0;
    lastVerifiedPanRef.current = pan;
    setPanLeadBlocked(false);
    setPanVerified(normalizedStatus === 1);
    setPanVerifyStatus(normalizedStatus);
    clearFieldError('panNumber');
    if (feedback) setPanStatusFeedback(feedback);
    return { blocked: false, checked: true, panVerifyStatus: normalizedStatus };
  };

  const verifyPanRequest = async (pan) => {
    const resp = await axios.post(
      loanBackendUrl('verifyPan.php'),
      { pan },
      { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
    );
    return normalizeKycApiResponse(resp.data);
  };

  const checkPan = async (panInput) => {
    const pan = String(panInput ?? step1.panNumber).trim().toUpperCase();
    const panError = validatePan(pan);

    if (pan.length !== 10) return { blocked: false, checked: false };

    if (panError) {
      setPanStatusFeedback(null);
      setFieldError('panNumber', panError);
      return { blocked: false, checked: false };
    }

    setPanChecking(true);
    setPanStatusFeedback(null);
    setPanVerifyStatus(0);
    setPanResponseData(null);
    setPanLeadBlocked(false);
    setPanVerified(false);
    clearFieldError('panNumber');

    try {
      // 1) Cross-vertical DISBURSED check FIRST (dhanwalle) — then other 3 APIs
      // try {
      //   const crossResp = await axios.get(crossVerticalPanUrl(pan), { timeout: 30000 });
      //   const crossVerticalData = crossResp.data;

      //   if (crossVerticalData?.status === false) {
      //     const apiMsg = getCrossVerticalApiMessage(
      //       crossVerticalData,
      //       null,
      //       'Unable to verify loan status across NBFCs. Please try again.'
      //     );
          
      //     // Agar message "PAN not found in any database." hai, toh ise block mat karo (fresh user hai)
      //     const isNotFoundMsg = String(apiMsg || '').toLowerCase().includes('pan not found in any database');
          
      //     if (!isNotFoundMsg) {
      //       return blockPanLead(pan, apiMsg, 0);
      //     }
      //     // Agar not found hai, toh chupchap aage badh jane do (koi error show mat karo)
      //   }

      //   // 2+ verticals already DISBURSED → do not allow 3rd apply
      //   if (isCrossVerticalDisbursedBlocked(crossVerticalData)) {
      //     return blockPanLead(pan, CROSS_VERTICAL_DISBURSED_BLOCK_MESSAGE, 0);
      //   }
      // } catch (crossErr) {
      //   console.error('Cross-vertical PAN check failed:', crossErr);
      //   const apiMsg = getCrossVerticalApiMessage(
      //     crossErr?.response?.data,
      //     crossErr,
      //     'Unable to verify loan status across NBFCs. Please try again.'
      //   );
      //   return blockPanLead(pan, apiMsg, 0);
      // }

      const cachedVerifyPan = readStoredPanVerify(pan);
      const [verifyPanResult, blacklistResp, customerResp] = await Promise.all([
        cachedVerifyPan
          ? Promise.resolve(cachedVerifyPan)
          : verifyPanRequest(pan)
              .then((result) => {
                writeStoredPanVerify(pan, result);
                return result;
              })
              .catch((err) => ({ failed: true, error: err })),
        axios
          .get(
            `${taskApiDirectUrl('checkGetCustomerbyPanBlacklist')}?pancard=${encodeURIComponent(pan)}`
          )
          .catch((err) => ({ failed: true, error: err })),
        axios
          .get(
            `${taskApiDirectUrl('checkGetCustomerbyPan')}?pancard=${encodeURIComponent(pan)}`
          )
          .catch((err) => ({ failed: true, error: err })),
      ]);

      if (cachedVerifyPan && verifyPanResult && !verifyPanResult.failed) {
        writeStoredPanVerify(pan, verifyPanResult);
      }

      if (blacklistResp?.failed) {
        console.warn('Blacklist check unavailable:', blacklistResp.error);
      }

      if (customerResp?.failed) {
        console.warn('Customer lookup unavailable:', customerResp.error);
      }

      const blacklistData = blacklistResp?.failed ? null : blacklistResp?.data;
      if (blacklistData?.blacklisted === true) {
        setPanVerifyStatus(0);
        setPanStatusFeedback({
          type: 'error',
          message: blacklistData.message || 'Customer is blacklisted.',
        });
        setFieldError('panNumber', blacklistData.message || 'Customer is blacklisted.');
        lastVerifiedPanRef.current = pan;
        return { blocked: true, checked: true };
      }

      if (isPanNotFoundResponse(verifyPanResult)) {
        return blockPanLead(pan, PAN_NOT_FOUND_MESSAGE, 0);
      }

      let verifyPanSuccess = false;

      if (isVerifyPanSuccess(verifyPanResult)) {
        const panData = verifyPanResult.data;
        setPanResponseData(verifyPanResult);
        setHiddenProfile((prev) => ({
          ...prev,
          firstName: panData.firstName || panData.first_name || prev.firstName,
          middleName: prev.middleName,
          lastName: panData.lastName || panData.last_name || prev.lastName,
          dob: panData.dateOfBirth || panData.dob || panData.date_of_birth || prev.dob,
          gender: panData.gender ? mapGender(panData.gender) : prev.gender,
          aadhaarLastFour:
            extractAadhaarLastFour(
              panData.maskedAadhaarNumber,
              panData.maskAadhaarNumber
            ) || prev.aadhaarLastFour,
        }));

        if (panData.address) {
          setStep2((prev) => ({
            ...prev,
            address: prev.address || panData.address,
          }));
        }

        verifyPanSuccess = true;
      } else if (verifyPanResult?.success === false || verifyPanResult?.status === false) {
        setPanResponseData(verifyPanResult);
        setPanStatusFeedback({
          type: 'warn',
          message:
            verifyPanResult.responseMessage ||
            verifyPanResult.message ||
            'PAN could not be verified online. You may continue with Aadhaar verification.',
        });
      } else if (verifyPanResult?.failed) {
        console.warn('verifyPan API unavailable:', verifyPanResult.error);
        setPanStatusFeedback({
          type: 'info',
          message: 'PAN verification service is temporarily unavailable. You may continue.',
        });
      } else if (verifyPanResult?.responseMessage) {
        // Nested/unknown failure — keep raw for payload debugging
        setPanResponseData(verifyPanResult);
        setPanStatusFeedback({
          type: 'warn',
          message: verifyPanResult.responseMessage,
        });
      }

      const customerData = customerResp?.failed
        ? customerResp?.error?.response?.data || null
        : customerResp?.data;

      const panVerifyFailFeedback = getPanVerifyFailureFeedback(verifyPanResult);

      // Govt PAN API failed (e.g. 406 Credits not available) — show THAT message, not "customer not found"
      if (panVerifyFailFeedback && !verifyPanSuccess) {
        setPanResponseData(verifyPanResult);

        // Still block if lead status forbids re-apply
        if (customerData?.status === true && customerData?.data) {
          const profile = mapCustomerToHiddenProfile(customerData.data);
          const leadDecision = getLeadStatusDecision(customerData.data);
          setHiddenProfile((prev) => ({
            firstName: profile.firstName || prev.firstName,
            middleName: profile.middleName || prev.middleName,
            lastName: profile.lastName || prev.lastName,
            dob: profile.dob || prev.dob,
            gender: profile.gender || prev.gender,
          }));
          if (!leadDecision.allowed) {
            return blockPanLead(pan, leadDecision.feedback.message, 0);
          }
        }

        if (isLoanBlockedResponse(customerData)) {
          return blockPanLead(
            pan,
            customerData?.message || 'Your loan status does not allow re-apply.',
            0
          );
        }

        return completePanCheck(pan, panVerifyFailFeedback, 0);
      }

      if (isPanNotFoundResponse(customerData)) {
        if (verifyPanSuccess) {
          return completePanCheck(
            pan,
            {
              type: 'info',
              message: 'No existing customer found. Continue with Aadhaar verification.',
            },
            1
          );
        }

        return blockPanLead(pan, PAN_NOT_FOUND_MESSAGE, 0);
      }

      if (customerData?.status === true && customerData?.data) {
        const profile = mapCustomerToHiddenProfile(customerData.data);
        const leadDecision = getLeadStatusDecision(customerData.data);

        setHiddenProfile((prev) => ({
          firstName: profile.firstName || prev.firstName,
          middleName: profile.middleName || prev.middleName,
          lastName: profile.lastName || prev.lastName,
          dob: profile.dob || prev.dob,
          gender: profile.gender || prev.gender,
        }));

        if (!leadDecision.allowed) {
          return blockPanLead(pan, leadDecision.feedback.message, verifyPanSuccess);
        }

        setPanStatusFeedback(leadDecision.feedback);
        return completePanCheck(pan, null, verifyPanSuccess ? 1 : 0);
      }

      if (isCustomerNotFoundResponse(customerData)) {
        if (verifyPanSuccess) {
          return completePanCheck(pan, {
            type: 'success',
            message: 'PAN verified successfully.',
          }, 1);
        }

        return completePanCheck(pan, {
          type: 'info',
          message: 'No existing customer found. Continue with Aadhaar verification.',
        }, 0);
      }

      if (isLoanBlockedResponse(customerData)) {
        return blockPanLead(
          pan,
          customerData?.message || 'Your loan status does not allow re-apply.',
          verifyPanSuccess
        );
      }

      if (verifyPanSuccess) {
        return completePanCheck(pan, {
          type: 'success',
          message: 'PAN verified successfully.',
        }, 1);
      }

      if (customerResp?.failed) {
        return completePanCheck(pan, {
          type: 'warn',
          message: 'Customer lookup is temporarily unavailable. You may continue with Aadhaar verification.',
        }, 0);
      }

      return completePanCheck(pan, {
        type: 'warn',
        message: customerData?.message || 'Unable to fetch full customer details. You may continue.',
      }, 0);
    } catch (err) {
      console.error('PAN check failed:', err);

      const status = err.response?.status;
      const responseData = err.response?.data;
      let message = '';

      if (typeof responseData === 'string' && responseData) {
        try {
          message = JSON.parse(responseData).message || responseData;
        } catch {
          message = responseData;
        }
      } else if (responseData?.message) {
        message = responseData.message;
      }

      if (status === 404 || isCustomerNotFoundResponse(responseData)) {
        return completePanCheck(pan, {
          type: 'info',
          message: 'No existing customer found. Continue with Aadhaar verification.',
        }, 0);
      }

      return completePanCheck(pan, {
        type: 'warn',
        message: message || 'Unable to verify PAN completely. You may continue.',
      }, 0);
    } finally {
      setPanChecking(false);
    }
  };

  const ensurePanChecked = async () => {
    const pan = step1.panNumber.trim().toUpperCase();
    if (!pan || pan.length !== 10) return { blocked: false, checked: false, panVerifyStatus: 0 };

    if (lastVerifiedPanRef.current === pan) {
      return {
        blocked: panLeadBlocked,
        checked: true,
        panVerifyStatus: panVerifyStatus === 1 ? 1 : 0,
      };
    }

    return checkPan(pan);
  };

  const sendAadhaarOtpRequest = async () => {
    const resp = await axios.post(
      loanBackendUrl('sendAadhaarOtp2.php'),
      {},
      {
        headers: { 'Content-Type': 'application/json' },
        timeout: 30000,
        validateStatus: () => true,
      }
    );
    return resp.data;
  };

  const fetchDigitapKycDetailsRequest = async (transactionId) => {
    const resp = await axios.get(getDigitapKycDetailsUrl(transactionId), {
      timeout: 30000,
      validateStatus: () => true,
    });
    return resp.data;
  };

  const stopDigitapPolling = () => {
    if (digitapPollRef.current) {
      window.clearInterval(digitapPollRef.current);
      digitapPollRef.current = null;
    }
  };

  const stopDigitapWindowWatch = () => {
    if (digitapWindowWatchRef.current) {
      window.clearInterval(digitapWindowWatchRef.current);
      digitapWindowWatchRef.current = null;
    }
  };

  const watchDigitapWindow = (win) => {
    stopDigitapWindowWatch();
    if (!win) return;

    digitapWindowWatchRef.current = window.setInterval(() => {
      let closed = false;
      try {
        closed = Boolean(win.closed);
      } catch {
        closed = true;
      }

      if (!closed) return;

      stopDigitapWindowWatch();
      if (otpVerifiedRef.current) return;

      stopDigitapPolling();
      setDigitapVerifying(false);
      setOtpApiMessage({
        type: 'info',
        message: 'DigiLocker tab closed without verification. Click Continue to open DigiLocker again.',
      });
    }, 800);
  };

  const applyDigitapAadhaarProfile = (aadhaarData) => {
    let { firstName, middleName, lastName } = parseFullName(aadhaarData.name || aadhaarData.fullName);
    let dob = aadhaarData.dateOfBirth || aadhaarData.dob || '';
    let gender = mapGender(aadhaarData.gender);

    firstName = firstName || hiddenProfile.firstName;
    middleName = middleName || hiddenProfile.middleName;
    lastName = lastName || hiddenProfile.lastName;
    dob = dob || hiddenProfile.dob;
    gender = gender || hiddenProfile.gender;

    const aadhaarLastFour =
      extractAadhaarLastFour(
        aadhaarData.maskAadhaarNumber,
        aadhaarData.maskedAadhaarNumber,
        aadhaarData.uid,
        aadhaarData.aadhaarLastFour,
        aadhaarData.lastFourDigits,
        aadhaarData.unique_id,
        aadhaarData.aadhaar_number,
        step1.aadhaarNumber
      ) || hiddenProfile.aadhaarLastFour;

    const aadhaarDigits = String(
      aadhaarData.aadhaar_number ||
        aadhaarData.uid ||
        aadhaarData.unique_id ||
        aadhaarData.aadhaarNumber ||
        ''
    ).replace(/\D/g, '');
    if (aadhaarDigits.length === 12) {
      setStep1((prev) => ({ ...prev, aadhaarNumber: aadhaarDigits }));
    }

    setHiddenProfile({
      firstName,
      middleName,
      lastName,
      dob,
      gender,
      aadhaarLastFour,
    });

    const aadhaarAddress = buildAadhaarAddress(aadhaarData);
    setStep2((prev) => ({
      ...prev,
      address: aadhaarAddress || prev.address,
      mobile:
        aadhaarData.mobile && MOBILE_REGEX.test(String(aadhaarData.mobile))
          ? String(aadhaarData.mobile)
          : prev.mobile,
      email: aadhaarData.email || prev.email,
    }));

    return { firstName, middleName, lastName };
  };

  const completeDigitapVerification = (data, transactionIdOverride = '') => {
    const message = formatApiMessage(
      data?.Message || data?.message || 'Aadhaar verified successfully.'
    );
    setOtpApiMessage({ type: 'success', message });

    const profile = extractDigitapProfile(data);
    if (profile) {
      applyDigitapAadhaarProfile(profile);
    }

    const resolvedTxnId = normalizeDigitapTransactionId(
      transactionIdOverride || digitapTransactionId
    );
    if (resolvedTxnId) {
      markDigitapCallbackProcessed(resolvedTxnId);
    }

    setOtpVerified(true);
    setDigitapPending(false);
    setDigitapVerifying(false);
    setDigitapKycUrl('');
    stopDigitapPolling();
    stopDigitapWindowWatch();
    clearFieldError('panNumber');
    flushApplyLoanDraftNow({
      mobileVerified: true,
      otpVerified: true,
      digitapPending: false,
      digitapTransactionId: '',
    });
  };

  const flushApplyLoanDraftNow = (overrides = {}) => {
    writeApplyLoanDraft({
      step1: {
        panNumber: step1.panNumber,
        aadhaarNumber: step1.aadhaarNumber,
        otp: '',
      },
      step2,
      hiddenProfile,
      mobileVerified,
      mobileOtpSent: mobileVerified ? false : mobileOtpSent,
      mobileOtpSentAt: mobileVerified ? 0 : mobileOtpSentAt,
      otpVerified,
      digitapPending,
      digitapTransactionId,
      stateId,
      cityId,
      panVerifyStatus,
      panResponseData,
      panVerified,
      panLeadBlocked,
      panStatusFeedback,
      lastVerifiedPan: lastVerifiedPanRef.current || '',
      ...overrides,
    });
  };

  const restoreFormFromDraft = (draft) => {
    if (!draft) return false;

    setStep1({
      ...initialStep1,
      ...(draft.step1 || {}),
      otp: '',
    });
    setStep2({
      ...initialStep2,
      ...(draft.step2 || {}),
      state: draft.step2?.state || '',
      city: draft.step2?.city || '',
      pincode: draft.step2?.pincode || '',
      termsAccepted: Boolean(draft.step2?.termsAccepted),
    });
    setHiddenProfile({
      ...initialHiddenProfile,
      ...(draft.hiddenProfile || {}),
    });
    setMobileVerified(Boolean(draft.mobileVerified) || Boolean(draft.otpVerified));
    setMobileOtpSent(false);
    setMobileOtp('');
    setPanVerified(Boolean(draft.panVerified));
    setPanVerifyStatus(draft.panVerifyStatus === 1 ? 1 : 0);
    setPanResponseData(draft.panResponseData || null);
    setPanLeadBlocked(Boolean(draft.panLeadBlocked));
    setPanStatusFeedback(draft.panStatusFeedback || null);
    if (draft.lastVerifiedPan) {
      lastVerifiedPanRef.current = String(draft.lastVerifiedPan);
    }
    if (draft.stateId) setStateId(String(draft.stateId));
    if (draft.cityId) setCityId(String(draft.cityId));
    return true;
  };

  const applyDigitapCallbackBootState = (normalizedTxnId) => {
    setMobileVerified(true);
    setDigitapTransactionId(normalizedTxnId);
    setDigitapPending(true);
    setDigitapReturnConfirmed(true);
    setDigitapVerifying(true);
  };

  const resumeAadhaarReturnCallback = async (txnId) => {
    const normalizedTxnId = normalizeDigitapTransactionId(txnId);
    if (!isValidDigitapTransactionId(normalizedTxnId)) {
      scrubApplyLoanUrl();
      setOtpApiMessage({
        type: 'error',
        message: 'Invalid Aadhaar verification link. Please verify Aadhaar again.',
      });
      return;
    }

    applyDigitapCallbackBootState(normalizedTxnId);
    scrubApplyLoanUrl();
    persistDigitapCallbackTxn(normalizedTxnId);

    const draft = readApplyLoanDraft();
    const pendingTxn = readDigitapPendingTxn();

    if (pendingTxn && pendingTxn !== normalizedTxnId) {
      if (draft) {
        restoreFormFromDraft(draft);
      }
      setOtpApiMessage({
        type: 'error',
        message: 'Aadhaar session mismatch. Please verify Aadhaar again on this device.',
      });
      setDigitapVerifying(false);
      return;
    }

    if (isDigitapCallbackProcessed(normalizedTxnId)) {
      if (draft) {
        restoreFormFromDraft(draft);
      }
      if (draft?.otpVerified) {
        setOtpVerified(true);
        setDigitapPending(false);
        setDigitapVerifying(false);
        return;
      }
    }

    if (draft) {
      restoreFormFromDraft(draft);
    }

    digitapPollAttemptsRef.current = 0;
    setOtpApiMessage({
      type: 'info',
      message: 'Aadhaar verified! Fetching your details from server...',
    });

    flushApplyLoanDraftNow({
      mobileVerified: true,
      digitapPending: true,
      digitapTransactionId: normalizedTxnId,
    });

    try {
      const data = await fetchDigitapKycDetailsRequest(normalizedTxnId);
      if (isDigitapKycComplete(data)) {
        completeDigitapVerification(data, normalizedTxnId);
        return;
      }
      if (isDigitapKycPending(data)) {
        pollDigitapKycStatus(normalizedTxnId, true);
        return;
      }
      pollDigitapKycStatus(normalizedTxnId, true);
    } catch {
      pollDigitapKycStatus(normalizedTxnId, true);
    }
  };

  useLayoutEffect(() => {
    const search = getBrowserSearch() || location.search || '';
    const { txnId, aadhaarVerified, failed } = resolveDigitapCallbackTxn(search);
    if (failed) {
      scrubApplyLoanUrl();
      clearDigitapCallbackTxn();
      setOtpApiMessage({
        type: 'error',
        message: 'Aadhaar verification was not completed. Please try again.',
      });
      return;
    }
    if (!txnId || !aadhaarVerified || otpVerified) {
      return;
    }
    if (digitapCallbackInitRef.current === txnId) {
      return;
    }
    digitapCallbackInitRef.current = txnId;
    applyDigitapCallbackBootState(normalizeDigitapTransactionId(txnId));
    resumeAadhaarReturnCallback(txnId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const pollDigitapKycStatus = (transactionId, fromCallback = false) => {
    stopDigitapPolling();
    digitapPollStartedRef.current = Date.now();
    digitapPollAttemptsRef.current = 0;

    const pollOnce = async () => {
      digitapPollAttemptsRef.current += 1;

      if (Date.now() - digitapPollStartedRef.current > DIGITAP_POLL_MAX_MS) {
        stopDigitapPolling();
        setDigitapVerifying(false);
        setOtpApiMessage({
          type: 'error',
          message: fromCallback || digitapReturnConfirmed
            ? 'Server has not returned Aadhaar KYC data yet. Please ask backend team to check getDigitapKycDetails API.'
            : 'Aadhaar verification timed out. Please try again.',
        });
        return;
      }

      try {
        const data = await fetchDigitapKycDetailsRequest(transactionId);
        if (isDigitapKycComplete(data)) {
          completeDigitapVerification(data, transactionId);
          return;
        }

        if (isDigitapKycPending(data)) {
          if (
            (fromCallback || digitapReturnConfirmed) &&
            digitapPollAttemptsRef.current >= 6
          ) {
            setOtpApiMessage({
              type: 'info',
              message:
                'Aadhaar verified on DigiLocker, but server KYC data is still pending. Waiting for backend...',
            });
          }
          return;
        }

        const status = String(data?.Status || data?.status || '').toLowerCase();
        const code = String(data?.data?.code || data?.code || '').toLowerCase();
        const terminal = ['failed', 'error', 'cancelled', 'canceled', 'rejected'].some((key) =>
          status.includes(key) || code.includes(key)
        );
        if (terminal) {
          stopDigitapPolling();
          setDigitapVerifying(false);
          setDigitapPending(false);
          const message = getDigitapApiMessage(
            data,
            'Aadhaar verification failed. Please try again.'
          );
          setOtpApiMessage({ type: 'error', message });
        }
      } catch {
        // keep polling on transient network errors
      }
    };

    pollOnce();
    digitapPollRef.current = window.setInterval(pollOnce, DIGITAP_POLL_MS);
  };

  const launchDigitapSession = (data, preOpenedWindow) => {
    const kycUrl = getDigitapKycUrl(data);
    const transactionId = getDigitapTransactionId(data);

    if (!kycUrl) {
      preOpenedWindow?.close();
      const message = getDigitapApiMessage(
        data,
        'Unable to start Aadhaar verification. Please try again.'
      );
      setOtpApiMessage({ type: 'error', message });
      setDigitapVerifying(false);
      setDigitapPending(false);
      return false;
    }

    setDigitapTransactionId(transactionId);
    setDigitapKycUrl(kycUrl);
    setDigitapPending(true);
    markDigitapPendingTxn(transactionId);
    flushApplyLoanDraftNow({
      digitapPending: true,
      digitapTransactionId: transactionId,
      mobileVerified: true,
    });
    setOtpApiMessage({
      type: 'success',
      message: 'DigiLocker opened in a new tab. Complete verification to continue.',
    });
    openDigitapWindow(kycUrl, transactionId, preOpenedWindow);
    return true;
  };

  const openDigitapWindow = (kycUrl, transactionId, preOpenedWindow = null) => {
    if (!kycUrl) {
      preOpenedWindow?.close();
      setDigitapVerifying(false);
      return null;
    }

    let win = preOpenedWindow;
    if (win && !win.closed) {
      try {
        win.location.replace(kycUrl);
      } catch {
        try {
          win.location.href = kycUrl;
        } catch {
          win = null;
        }
      }
    }

    if (!win) {
      win = window.open(kycUrl, '_blank', 'noopener,noreferrer');
    }

    digitapWindowRef.current = win;

    if (!win) {
      setDigitapVerifying(false);
      setOtpApiMessage({
        type: 'warn',
        message: 'Popup blocked. Allow popups for this site, then click Continue again.',
      });
      return null;
    }

    watchDigitapWindow(win);

    if (transactionId) {
      pollDigitapKycStatus(transactionId);
    } else {
      setDigitapVerifying(false);
      setOtpApiMessage({
        type: 'info',
        message: 'Complete verification in DigiLocker, then click Continue again.',
      });
    }

    return win;
  };

  const handleDigitapVerify = async () => {
    if (panChecking || digitapVerifying) return;

    if (digitapPending && digitapKycUrl) {
      setDigitapVerifying(true);
      setOtpApiMessage({
        type: 'info',
        message: 'Complete Aadhaar verification in the DigiLocker window. Keep this page open.',
      });
      openDigitapWindow(digitapKycUrl, digitapTransactionId);
      return;
    }

    if (!validateIdentityFields()) return;
    if (!panVerified) return;

    const preOpenedWindow = window.open('about:blank', '_blank');

    const panResult = await ensurePanChecked();
    if (!panResult.checked || panResult.blocked || panResult.panVerifyStatus !== 1) {
      preOpenedWindow?.close();
      return;
    }

    setDigitapVerifying(true);
    setOtpApiMessage(null);
    stopDigitapPolling();

    try {
      const data = await sendAadhaarOtpRequest();
      launchDigitapSession(data, preOpenedWindow);
    } catch (err) {
      const fallbackData = err.response?.data || err.data || null;
      if (fallbackData && launchDigitapSession(fallbackData, preOpenedWindow)) {
        return;
      }
      preOpenedWindow?.close();
      const message = getDigitapApiMessage(
        fallbackData,
        err.message || 'Unable to start Aadhaar verification. Please try again.'
      );
      setOtpApiMessage({ type: 'error', message });
      setDigitapVerifying(false);
      setDigitapPending(false);
    }
  };

  const loadCitiesForState = async (sid) => {
    if (!sid) {
      setCities([]);
      setCitiesLoadError('');
      setLoadingCities(false);
      return [];
    }

    const requestId = ++citiesRequestRef.current;
    setLoadingCities(true);
    setCitiesLoadError('');

    try {
      const resp = await axios.post(
        taskApiPostUrl('getCity'),
        { state_id: String(sid) },
        { headers: { 'Content-Type': 'application/json' }, timeout: 20000 }
      );

      if (requestId !== citiesRequestRef.current) return [];

      if (resp.data.Status === 1) {
        const cityData = Array.isArray(resp.data.Data) ? resp.data.Data : [];
        setCities(cityData);
        if (!cityData.length) {
          setCitiesLoadError('No cities found for this state.');
        }
        return cityData;
      }

      // API returned failure — try local fallback
      const fallback = getCitiesForState(sid);
      setCities(fallback);
      if (!fallback.length) {
        setCitiesLoadError(formatApiMessage(resp.data.Message) || 'Failed to load cities.');
      }
      return fallback;
    } catch (err) {
      console.error('Failed fetching cities:', err);
      if (requestId !== citiesRequestRef.current) return [];

      const fallback = getCitiesForState(sid);
      setCities(fallback);
      if (!fallback.length) {
        setCitiesLoadError('Could not load cities. Please try another state or refresh.');
      } else {
        setCitiesLoadError('');
      }
      return fallback;
    } finally {
      if (requestId === citiesRequestRef.current) {
        setLoadingCities(false);
      }
    }
  };

  const loadPincodesForCity = async (cid) => {
    if (!cid) {
      setPincodeList([]);
      setLoadingPincodes(false);
      return [];
    }

    const requestId = ++pincodesRequestRef.current;
    setLoadingPincodes(true);

    try {
      const resp = await axios.post(
        taskApiPostUrl('getPincodeByCityId'),
        { city_id: String(cid) },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (requestId !== pincodesRequestRef.current) return [];

      if (resp.data.Status === 1) {
        const pinData = Array.isArray(resp.data.Data) ? resp.data.Data : [];
        setPincodeList(pinData);
        return pinData;
      }

      setPincodeList([]);
      return [];
    } catch (err) {
      console.error('Failed fetching pincodes:', err);
      if (requestId === pincodesRequestRef.current) {
        setPincodeList([]);
      }
      return [];
    } finally {
      if (requestId === pincodesRequestRef.current) {
        setLoadingPincodes(false);
      }
    }
  };

  const handleStateChange = (sid) => {
    const selectedState = states.find((s) => String(s.state_id) === String(sid));

    setStep2((prev) => ({
      ...prev,
      state: selectedState?.state || '',
      city: '',
      pincode: '',
    }));
    setStateId(sid ? String(sid) : '');
    setCityId('');
    setPincodeList([]);
    setCitiesLoadError('');
    clearFieldError('state');
    clearFieldError('city');
    clearFieldError('pincode');
  };

  const handleCityChange = (cid) => {
    const selectedCity = cities.find((c) => String(c.city_id) === String(cid));

    setStep2((prev) => ({
      ...prev,
      city: selectedCity?.city || '',
      pincode: '',
    }));
    setCityId(cid ? String(cid) : '');
    clearFieldError('city');
    clearFieldError('pincode');
  };

  const handlePincodeChange = (pincodeValue) => {
    setStep2((prev) => ({ ...prev, pincode: pincodeValue }));
    clearFieldError('pincode');
  };

  useEffect(() => {
    if (!stateId) {
      setCities([]);
      setLoadingCities(false);
      return;
    }

    loadCitiesForState(stateId);
  }, [stateId]);

  useEffect(() => {
    if (!cityId) {
      setPincodeList([]);
      setLoadingPincodes(false);
      return;
    }

    loadPincodesForCity(cityId);
  }, [cityId]);

  const validateStep2 = () => {
    const fields = [
      'monthlyIncome',
      'loanAmount',
      'mobile',
      'altMobile',
      'email',
      'officialEmail',
      'obligations',
      'state',
      'city',
      'pincode',
      'address',
      'termsAccepted',
    ];

    const nextErrors = {};

    fields.forEach((field) => {
      let message = '';

      switch (field) {
        case 'monthlyIncome':
          message = validateAmount(step2.monthlyIncome, {
            label: 'Monthly income',
            min: MIN_MONTHLY_INCOME,
            max: MAX_MONTHLY_INCOME,
          });
          break;
        case 'loanAmount':
          message = validateAmount(step2.loanAmount, {
            label: 'Loan amount',
            min: MIN_LOAN_AMOUNT,
            max: MAX_LOAN_AMOUNT,
          });
          if (!message && step2.monthlyIncome && ONLY_DIGITS.test(step2.monthlyIncome)) {
            const income = Number(step2.monthlyIncome);
            const loan = Number(step2.loanAmount);
            if (loan > income * 5) {
              message = 'Loan amount cannot exceed 5Ã— your monthly income';
            }
          }
          break;
        case 'mobile':
          message = validateMobile(step2.mobile);
          break;
        case 'altMobile':
          message = validateMobile(step2.altMobile, {
            required: false,
            label: 'Alternate mobile',
          });
          if (!message && step2.altMobile && step2.altMobile === step2.mobile) {
            message = 'Alternate mobile must be different from mobile';
          }
          break;
        case 'email':
          message = validateEmail(step2.email);
          break;
        case 'officialEmail':
          message = validateEmail(step2.officialEmail, {
            required: false,
            label: 'Official email',
          });
          if (
            !message &&
            step2.officialEmail.trim() &&
            step2.email.trim() &&
            step2.officialEmail.trim().toLowerCase() === step2.email.trim().toLowerCase()
          ) {
            message = 'Official email must be different from personal email';
          }
          break;
        case 'obligations':
          if (step2.obligations) {
            message = validateAmount(step2.obligations, {
              required: false,
              label: 'Obligations',
              min: 0,
              max: MAX_OBLIGATIONS,
              allowZero: true,
            });
            if (
              !message &&
              step2.monthlyIncome &&
              ONLY_DIGITS.test(step2.monthlyIncome) &&
              Number(step2.obligations) > Number(step2.monthlyIncome)
            ) {
              message = 'Obligations cannot exceed monthly income';
            }
          }
          break;
        case 'state':
          if (!stateId || !step2.state) message = 'State is required';
          break;
        case 'city':
          if (!cityId || !step2.city) message = 'City is required';
          break;
        case 'pincode':
          message = validatePincode(step2.pincode);
          if (!message && cityId && pincodeList.length > 0) {
            const pinExists = pincodeList.some(
              (pin) => String(pin.m_pincode_value) === String(step2.pincode)
            );
            if (!pinExists) message = 'Please select a valid pincode';
          } else if (!message && cityId && pincodeList.length === 0) {
            message = 'No pincodes available for selected city';
          }
          break;
        case 'address':
          message = validateAddress(step2.address);
          break;
        case 'termsAccepted':
          if (!step2.termsAccepted) message = 'Please accept terms & conditions';
          break;
        default:
          break;
      }

      if (message) nextErrors[field] = message;
    });

    setErrors((prev) => {
      const updated = { ...prev };
      fields.forEach((field) => delete updated[field]);
      return { ...updated, ...nextErrors };
    });
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Sync lock — blocks double-click before React re-renders disabled button
    if (submittingLockRef.current || submitting || submitted) {
      return;
    }

    setSubmitError('');

    if (!mobileVerified) {
      setSubmitError('Please verify your mobile number before submitting.');
      return;
    }

    if (!otpVerified) {
      setSubmitError('Please complete Aadhaar DigiLocker verification before submitting.');
      return;
    }

    if (!validateStep2()) {
      setSubmitError('Please fix the highlighted fields before submitting.');
      return;
    }

    if (!stateId || !cityId) {
      if (!stateId) setFieldError('state', 'State is required');
      if (!cityId) setFieldError('city', 'City is required');
      setSubmitError('Please select state and city.');
      return;
    }

    if (!hiddenProfile.firstName || !hiddenProfile.dob || !hiddenProfile.gender) {
      setSubmitError('Identity details missing. Please verify Aadhaar with DigiLocker again.');
      return;
    }

    submittingLockRef.current = true;
    setSubmitting(true);

    const unlockSubmit = () => {
      submittingLockRef.current = false;
      setSubmitting(false);
    };

    const pan = step1.panNumber.trim().toUpperCase();
    try {
      const panResult = await ensurePanChecked();
      if (!panResult.checked) {
        setSubmitError('Please enter and verify PAN before submitting.');
        unlockSubmit();
        return;
      }

      if (panResult.blocked || panLeadBlocked) {
        setSubmitError(
          panStatusFeedback?.message || 'Your loan is in progress. Cannot submit a new application.'
        );
        unlockSubmit();
        return;
      }

      // Reuse first verifyPan response from state / localStorage — do not hit API again on submit
      const cachedPanResponse =
        panResponseData || readStoredPanVerify(pan) || null;
      const submitPanVerifyStatus =
        panResult.panVerifyStatus === 1 ||
        panVerifyStatus === 1 ||
        isVerifyPanSuccess(cachedPanResponse)
          ? 1
          : 0;
      const submitPanResponseData = cachedPanResponse;

      if (submitPanResponseData && !submitPanResponseData.failed) {
        writeStoredPanVerify(pan, submitPanResponseData);
      }
      if (submitPanResponseData && !panResponseData) {
        setPanResponseData(submitPanResponseData);
      }
      setPanVerifyStatus(submitPanVerifyStatus);

      const payload = {
        loan_amount: Number(step2.loanAmount) || String(step2.loanAmount).trim(),
        monthly_income: String(step2.monthlyIncome).trim(),
        first_name: hiddenProfile.firstName.trim(),
        middle_name: (hiddenProfile.middleName || '').trim(),
        sur_name: (hiddenProfile.lastName || '').trim(),
        pan,
        pan_status: submitPanVerifyStatus,
        ...buildPanPayloadFields(submitPanResponseData),
        dob: hiddenProfile.dob,
        gender: String(hiddenProfile.gender).toUpperCase(),
        mobile: step2.mobile.trim(),
        alternate_mobile: (step2.altMobile || '').trim(),
        email_personal: step2.email.trim(),
        email_official: (step2.officialEmail || '').trim(),
        obligations: step2.obligations ? Number(step2.obligations) : 0,
        state_id: Number(stateId) || String(stateId),
        city: String(cityId),
        city_id: Number(cityId) || String(cityId),
        pin: String(step2.pincode).trim(),
        pincode: String(step2.pincode).trim(),
        address: step2.address.trim(),
        term_and_condition: step2.termsAccepted ? 'YES' : 'NO',
        ip: '106.219.215.60',
        coordinates: '',
        source: 'MrRupya',
        lead_data_source_id: 39,
        utm_source: '',
        utm_campaign: '',
        selfie_image_url: '',
        aadhaar_number: step1.aadhaarNumber.trim(),
        aadhaar_last_four:
          hiddenProfile.aadhaarLastFour || extractAadhaarLastFour(step1.aadhaarNumber),
      };

      const resp = await axios.post(saveLendingPageUrl(), payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 30000,
      });

      if (isLeadSaveSuccess(resp.data)) {
        const insertedId = getLeadInsertedId(resp.data);
        stopDigitapPolling();
        clearApplyLoanSessionAfterSubmit(digitapTransactionId);
        setSubmittedSummary({
          firstName: hiddenProfile.firstName.trim(),
          loanAmount: String(step2.loanAmount).trim(),
          mobile: step2.mobile.trim(),
          ref: insertedId ? `MR${insertedId}` : `MR${Date.now().toString().slice(-8)}`,
        });
        setSubmitted(true);
        setSubmitError('');
        // Keep lock + submitting true so another lead cannot be posted
        return;
      }

      setSubmitError(getLeadSaveMessage(resp?.data, 'Submit failed. Please try again.'));
      unlockSubmit();
    } catch (err) {
      let message = 'Submit failed. Please try again.';

      if (err.response) {
        message =
          getLeadSaveMessage(err.response.data) ||
          `Server error (${err.response.status}). Please try again.`;
      } else if (err.request) {
        message = 'Network error. Please check your connection and try again.';
      } else if (err.message) {
        message = err.message;
      }

      setSubmitError(message);
      unlockSubmit();
    }
  };

  const panValid = !validatePan(step1.panNumber);
  const mobileValid = MOBILE_REGEX.test(step2.mobile);
  const altMobileValid = !step2.altMobile || MOBILE_REGEX.test(step2.altMobile);
  const emailValid = !validateEmail(step2.email);

  const renderCustomDropdown = (props) => <CustomDropdown {...props} error={errors[props.id]} />;

  const renderTextareaField = (id, label, required, icon, value, onChange, options = {}) => (
    <div className={`apply-mrrupya-field apply-mrrupya-field-textarea ${errors[id] ? 'has-error' : ''}`}>
      <label htmlFor={id}>
        {label} {required && <span>*</span>}
      </label>
      <div className="apply-mrrupya-textarea-wrap">
        <span className="apply-mrrupya-textarea-icon">{icon}</span>
        <textarea
          id={id}
          value={value}
          onChange={onChange}
          onBlur={options.onBlur}
          placeholder={options.placeholder}
          maxLength={options.maxLength}
          rows={3}
        />
      </div>
      {errors[id] && !options.hideError && <span className="apply-mrrupya-error">{errors[id]}</span>}
    </div>
  );

  const renderInputField = (id, label, required, icon, value, onChange, options = {}) => (
    <div className={`apply-mrrupya-field ${errors[id] ? 'has-error' : ''}`}>
      <label htmlFor={id}>
        {label} {required && <span>*</span>}
      </label>
      <div className={`apply-mrrupya-input-wrap ${options.valid ? 'is-valid' : ''}`}>
        <span className="apply-mrrupya-input-icon">{icon}</span>
        <input
          id={id}
          name={options.name || id}
          value={value}
          onChange={onChange}
          onBlur={options.onBlur}
          placeholder={options.placeholder}
          maxLength={options.maxLength}
          inputMode={options.inputMode}
          disabled={options.disabled}
          autoComplete={options.autoComplete || 'off'}
          autoCorrect={options.autoCorrect || 'off'}
          spellCheck={options.spellCheck === true}
          type={options.type || 'text'}
        />
        {options.valid && (
          <span className="apply-mrrupya-input-check">
            <FiCheckCircle />
          </span>
        )}
      </div>
      {errors[id] && !options.hideError && <span className="apply-mrrupya-error">{errors[id]}</span>}
    </div>
  );

  return (
    <div className="apply-mrrupya-page">
      <div className="apply-mrrupya-shell">
        <div className="apply-mrrupya-card">
          <aside className="apply-mrrupya-visual" aria-label="Apply for MR Rupya loan">
            <img
              src={applyNowImg}
              alt="Ready to take the next step — apply for your MR Rupya loan"
              className="apply-mrrupya-visual-img"
            />
          </aside>

          <section className="apply-mrrupya-form-panel">
            {submitted ? (
              <div className="apply-mrrupya-success">
                <div className="apply-mrrupya-success-confetti" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>

                <div className="apply-mrrupya-success-hero">
                  <div className="apply-mrrupya-success-icon-wrap">
                    <span className="apply-mrrupya-success-ring" />
                    <span className="apply-mrrupya-success-ring apply-mrrupya-success-ring--delay" />
                    <div className="apply-mrrupya-success-icon">
                      <FiCheckCircle />
                    </div>
                  </div>

                  <span className="apply-mrrupya-success-badge">Application Received</span>
                  <h3>
                    Thank You
                    {submittedSummary?.firstName ? `, ${submittedSummary.firstName}` : ''}!
                  </h3>
                  <p>
                    Your loan application has been submitted successfully. Keep your phone handy —
                    our team will reach out shortly with an update.
                  </p>
                </div>

                {submittedSummary && (
                  <div className="apply-mrrupya-success-summary">
                    <div className="apply-mrrupya-success-summary-item">
                      <small>Loan Amount</small>
                      <strong>{`\u20B9${formatIndianCurrency(submittedSummary.loanAmount)}`}</strong>
                    </div>
                    <div className="apply-mrrupya-success-summary-item">
                      <small>Registered Mobile</small>
                      <strong>+91 {maskMobileNumber(submittedSummary.mobile)}</strong>
                    </div>
                    <div className="apply-mrrupya-success-summary-item">
                      <small>Reference No.</small>
                      <strong>{submittedSummary.ref}</strong>
                    </div>
                  </div>
                )}

                <div className="apply-mrrupya-success-steps">
                  <h4>What happens next?</h4>
                  <ol>
                    {SUCCESS_STEPS.map((item, index) => (
                      <li key={item.title}>
                        <span className="apply-mrrupya-success-step-num">{index + 1}</span>
                        <span>
                          <strong>{item.title}</strong>
                          <small>{item.text}</small>
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="apply-mrrupya-success-actions">
                  <Link to="/" className="apply-mrrupya-btn apply-mrrupya-btn-primary">
                    <FiHome /> Back to Home
                  </Link>
                  <a href="tel:+918796447298" className="apply-mrrupya-btn apply-mrrupya-btn-outline">
                    <FiPhone /> Call Support
                  </a>
                </div>

                <div className="apply-mrrupya-success-support">
                  <FiMail />
                  <span>
                    Need help? Email us at{' '}
                    <a href="mailto:info@mrrupya.com">info@mrrupya.com</a>
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div className="apply-mrrupya-stepper">
                  <div
                    className={`apply-mrrupya-stepper-item ${!mobileVerified ? 'active' : ''} ${mobileVerified ? 'done' : ''}`}
                  >
                    <span className="apply-mrrupya-stepper-dot">1</span>
                    <span className="apply-mrrupya-stepper-text">
                      <small>Step 1</small>
                      Mobile
                    </span>
                  </div>
                  <div className="apply-mrrupya-stepper-line" />
                  <div
                    className={`apply-mrrupya-stepper-item ${mobileVerified && !otpVerified ? 'active' : ''} ${otpVerified ? 'done' : ''}`}
                  >
                    <span className="apply-mrrupya-stepper-dot">2</span>
                    <span className="apply-mrrupya-stepper-text">
                      <small>Step 2</small>
                      Verification
                    </span>
                  </div>
                  <div className="apply-mrrupya-stepper-line" />
                  <div className={`apply-mrrupya-stepper-item ${otpVerified ? 'active' : ''}`}>
                    <span className="apply-mrrupya-stepper-dot">3</span>
                    <span className="apply-mrrupya-stepper-text">
                      <small>Step 3</small>
                      Loan Details
                    </span>
                  </div>
                </div>

                <div className="apply-mrrupya-form-header">
                  <h3>
                    {!mobileVerified
                      ? 'Verify your mobile'
                      : otpVerified
                        ? 'Loan details'
                        : 'Verify your identity'}
                  </h3>
                  <p>
                    {!mobileVerified
                      ? 'Enter your mobile number and verify OTP to continue.'
                      : otpVerified
                        ? 'Share your income and contact details to complete the application.'
                        : 'Verify PAN, then continue to DigiLocker'}
                  </p>
                </div>

                {showResumeNote && (
                  <div className="apply-mrrupya-otp-message is-info" style={{ marginBottom: 12 }}>
                    <span>
                      Your previous progress was restored. Continue from where you left off.
                    </span>
                  </div>
                )}

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!mobileVerified) {
                      if (mobileOtpSent) handleVerifyMobileOtp();
                      else handleSendMobileOtp();
                      return;
                    }
                    if (otpVerified) {
                      if (submittingLockRef.current || submitting || submitted) return;
                      handleSubmit(e);
                      return;
                    }
                    handleDigitapVerify();
                  }}
                >
                  <div
                    className={`apply-mrrupya-grid ${otpVerified ? '' : 'apply-mrrupya-grid-single'}`}
                  >
                    {!mobileVerified && (
                      <>
                        {renderInputField(
                          'mobile',
                          'Mobile Number',
                          true,
                          <FiPhone />,
                          step2.mobile,
                          handleStep2Change,
                          {
                            placeholder: '10-digit mobile number',
                            maxLength: 10,
                            inputMode: 'numeric',
                            autoComplete: 'tel-national',
                            name: 'mobile',
                            valid: MOBILE_REGEX.test(step2.mobile),
                            disabled: sendingMobileOtp || verifyingMobileOtp || mobileOtpSent,
                            hideError: true,
                            onBlur: () => {
                              const message = validateMobile(step2.mobile);
                              if (message) setFieldError('mobile', message);
                              else clearFieldError('mobile');
                            },
                          }
                        )}
                        {!mobileOtpSent && mobileOtpMessage && (
                          <div className={`apply-mrrupya-otp-message is-${mobileOtpMessage.type}`}>
                            <span>{mobileOtpMessage.message}</span>
                          </div>
                        )}
                        {!mobileOtpSent && errors.mobile && !mobileOtpMessage && (
                          <span className="apply-mrrupya-otp-message is-error">{errors.mobile}</span>
                        )}

                        {mobileOtpSent && (
                          <div className="full-width apply-mrrupya-otp-block">
                            <p className="apply-mrrupya-otp-message is-info" style={{ marginBottom: 8 }}>
                              OTP sent to {maskMobileNumber(step2.mobile)}
                            </p>
                            {renderInputField(
                              'mobileOtp',
                              'Enter OTP',
                              true,
                              <FiLock />,
                              mobileOtp,
                              (e) => handleMobileOtpChange(e.target.value),
                              {
                                placeholder: '6-digit OTP',
                                maxLength: 6,
                                inputMode: 'numeric',
                                autoComplete: 'one-time-code',
                                name: 'one-time-code',
                                valid: mobileOtp.length === 6 && !validateOtp(mobileOtp),
                                hideError: true,
                              }
                            )}

                            {mobileOtpMessage && (
                              <div className={`apply-mrrupya-otp-message is-${mobileOtpMessage.type}`}>
                                <span>{mobileOtpMessage.message}</span>
                              </div>
                            )}

                            {errors.mobileOtp && !mobileOtpMessage && (
                              <span className="apply-mrrupya-otp-message is-error">
                                {errors.mobileOtp}
                              </span>
                            )}

                            <div className="apply-mrrupya-otp-actions">
                              {mobileOtpCountdown > 0 ? (
                                <span className="apply-mrrupya-otp-timer">
                                  Resend OTP in {formatCountdown(mobileOtpCountdown)}
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  className="apply-mrrupya-otp-resend"
                                  onClick={() => handleSendMobileOtp(true)}
                                  disabled={sendingMobileOtp || verifyingMobileOtp}
                                >
                                  {sendingMobileOtp ? 'Sending OTP...' : 'Resend OTP'}
                                </button>
                              )}
                              <button
                                type="button"
                                className="apply-mrrupya-otp-resend"
                                onClick={() => {
                                  setMobileOtpSent(false);
                                  setMobileOtpSentAt(0);
                                  setMobileOtp('');
                                  setMobileOtpMessage(null);
                                  setMobileOtpCountdown(0);
                                  clearFieldError('mobileOtp');
                                }}
                                disabled={sendingMobileOtp || verifyingMobileOtp}
                              >
                                Change number
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {mobileVerified && !otpVerified && (
                      <>
                        {renderInputField(
                          'panNumber',
                          'PAN Number',
                          true,
                          <FiCreditCard />,
                          step1.panNumber,
                          (e) => handleStep1Change('panNumber', e.target.value),
                          {
                            placeholder: 'ABCDE1234F',
                            maxLength: 10,
                            valid: panValid && panVerified,
                            disabled: panChecking,
                            hideError: true,
                            onBlur: () => handleStep1Blur('panNumber'),
                          }
                        )}
                        {panChecking && (
                          <span className="apply-mrrupya-info apply-mrrupya-pan-message">Checking PAN...</span>
                        )}
                        {!panChecking && panStatusFeedback && (
                          <span className={`apply-mrrupya-pan-message is-${panStatusFeedback.type}`}>
                            {panStatusFeedback.message}
                          </span>
                        )}
                        {!panChecking && !panStatusFeedback && errors.panNumber && (
                          <span className="apply-mrrupya-pan-message is-error">
                            {errors.panNumber}
                          </span>
                        )}

                        {panVerified && !digitapPending && (
                          <p className="apply-mrrupya-mobile-edit-hint">
                            PAN verified. Continue to complete Aadhaar verification in DigiLocker.
                          </p>
                        )}
                        {digitapPending && (
                          <p className="apply-mrrupya-mobile-edit-hint">
                            Complete Aadhaar verification in the DigiLocker window. Keep this page open.
                          </p>
                        )}
                        {otpApiMessage && (
                          <div className={`apply-mrrupya-otp-message is-${otpApiMessage.type}`}>
                            <span>{otpApiMessage.message}</span>
                          </div>
                        )}
                      </>
                    )}

                    {otpVerified && (
                      <>
                        {renderInputField(
                          'fullName',
                          'Full Name',
                          true,
                          <FiUser />,
                          formatHiddenFullName(hiddenProfile),
                          () => {},
                          {
                            placeholder: 'Name from Aadhaar',
                            disabled: true,
                            valid: Boolean(formatHiddenFullName(hiddenProfile)),
                          }
                        )}

                        {renderInputField(
                          'monthlyIncome',
                          'Monthly Income',
                          true,
                          <LuIndianRupee />,
                          step2.monthlyIncome,
                          handleStep2Change,
                          {
                            placeholder: `Min \u20B9${formatIndianCurrency(MIN_MONTHLY_INCOME)}`,
                            inputMode: 'numeric',
                            maxLength: 10,
                            valid: step2.monthlyIncome && !validateAmount(step2.monthlyIncome, {
                              label: 'Monthly income',
                              min: MIN_MONTHLY_INCOME,
                              max: MAX_MONTHLY_INCOME,
                            }),
                            onBlur: () => handleStep2Blur('monthlyIncome'),
                          }
                        )}

                        {renderInputField(
                          'loanAmount',
                          'Loan Amount',
                          true,
                          <LuIndianRupee />,
                          step2.loanAmount,
                          handleStep2Change,
                          {
                            placeholder: `\u20B9${formatIndianCurrency(MIN_LOAN_AMOUNT)} - \u20B9${formatIndianCurrency(MAX_LOAN_AMOUNT)}`,
                            inputMode: 'numeric',
                            maxLength: 10,
                            valid: step2.loanAmount && !validateAmount(step2.loanAmount, {
                              label: 'Loan amount',
                              min: MIN_LOAN_AMOUNT,
                              max: MAX_LOAN_AMOUNT,
                            }),
                            onBlur: () => handleStep2Blur('loanAmount'),
                          }
                        )}

                        {renderInputField(
                          'mobile',
                          'Mobile',
                          true,
                          <FiPhone />,
                          step2.mobile,
                          handleStep2Change,
                          {
                            placeholder: '10-digit mobile number',
                            maxLength: 10,
                            inputMode: 'numeric',
                            valid: mobileValid,
                            disabled: mobileVerified,
                            onBlur: () => handleStep2Blur('mobile'),
                          }
                        )}

                        {renderInputField(
                          'altMobile',
                          'Alternate Mobile',
                          false,
                          <FiPhone />,
                          step2.altMobile,
                          handleStep2Change,
                          {
                            placeholder: 'Optional',
                            maxLength: 10,
                            inputMode: 'numeric',
                            valid: altMobileValid,
                            onBlur: () => handleStep2Blur('altMobile'),
                          }
                        )}

                        {renderInputField(
                          'email',
                          'Email',
                          true,
                          <FiMail />,
                          step2.email,
                          handleStep2Change,
                          {
                            placeholder: 'name@email.com',
                            maxLength: MAX_EMAIL_LENGTH,
                            valid: emailValid,
                            onBlur: () => handleStep2Blur('email'),
                          }
                        )}

                        {renderInputField(
                          'officialEmail',
                          'Official Email',
                          false,
                          <FiMail />,
                          step2.officialEmail,
                          handleStep2Change,
                          {
                            placeholder: 'work@company.com',
                            maxLength: MAX_EMAIL_LENGTH,
                            valid: step2.officialEmail && !validateEmail(step2.officialEmail, {
                              required: false,
                              label: 'Official email',
                            }),
                            onBlur: () => handleStep2Blur('officialEmail'),
                          }
                        )}

                        {renderInputField(
                          'obligations',
                          'Obligations',
                          false,
                          <LuIndianRupee />,
                          step2.obligations,
                          handleStep2Change,
                          {
                            placeholder: 'Monthly EMIs / obligations',
                            inputMode: 'numeric',
                            maxLength: 10,
                            onBlur: () => handleStep2Blur('obligations'),
                          }
                        )}

                        {renderCustomDropdown({
                          id: 'state',
                          label: 'State',
                          required: true,
                          icon: <FiMapPin />,
                          value: stateId,
                          placeholder: 'Select state',
                          options: states.map((state) => ({
                            value: String(state.state_id),
                            label: state.state,
                          })),
                          onChange: handleStateChange,
                        })}
                        {statesLoadError && (
                          <span className="apply-mrrupya-error apply-mrrupya-states-error">
                            {statesLoadError}
                          </span>
                        )}

                        {renderCustomDropdown({
                          id: 'city',
                          label: 'City',
                          required: true,
                          icon: <FiMapPin />,
                          value: cityId,
                          placeholder: !stateId
                            ? 'Select state first'
                            : loadingCities
                              ? 'Loading cities...'
                              : cities.length > 0
                                ? 'Select city'
                                : 'No cities available',
                          disabled: !stateId || loadingCities,
                          options: cities.map((city) => ({
                            value: String(city.city_id),
                            label: city.city,
                          })),
                          onChange: handleCityChange,
                        })}
                        {citiesLoadError && (
                          <span className="apply-mrrupya-error apply-mrrupya-states-error">
                            {citiesLoadError}
                          </span>
                        )}

                        {renderCustomDropdown({
                          id: 'pincode',
                          label: 'Pincode',
                          required: true,
                          icon: <FiMapPin />,
                          value: step2.pincode,
                          placeholder: !cityId
                            ? 'Select city first'
                            : loadingPincodes
                              ? 'Loading pincodes...'
                              : pincodeList.length > 0
                                ? 'Select pincode'
                                : 'No pincodes available',
                          disabled: !cityId || loadingPincodes || pincodeList.length === 0,
                          searchable: pincodeList.length > 6,
                          options: pincodeList.map((pin) => ({
                            value: String(pin.m_pincode_value),
                            label: String(pin.m_pincode_value),
                          })),
                          onChange: handlePincodeChange,
                        })}

                        {renderTextareaField(
                          'address',
                          'Address',
                          true,
                          <FiMapPin />,
                          step2.address,
                          handleStep2Change,
                          {
                            placeholder: 'House no., street, area, landmark (min 15 chars)',
                            maxLength: MAX_ADDRESS_LENGTH,
                            onBlur: () => handleStep2Blur('address'),
                          }
                        )}

                        <div className="full-width">
                          <label className={`apply-mrrupya-terms ${errors.termsAccepted ? 'has-error' : ''}`}>
                            <input
                              id="termsAccepted"
                              type="checkbox"
                              checked={step2.termsAccepted}
                              onChange={handleStep2Change}
                            />
                            <span>
                              I agree to the{' '}
                              <a
                                href="/terms-conditions"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Terms &amp; Conditions
                              </a>{' '}
                              and authorize MR Rupya to contact me regarding my loan application.
                            </span>
                          </label>
                          {errors.termsAccepted && (
                            <span className="apply-mrrupya-error">{errors.termsAccepted}</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {!mobileVerified && (
                    <div className="apply-mrrupya-send-wrap">
                      <button
                        type="submit"
                        className="apply-mrrupya-btn apply-mrrupya-btn-black"
                        disabled={sendingMobileOtp || verifyingMobileOtp}
                      >
                        {sendingMobileOtp
                          ? 'Sending OTP...'
                          : verifyingMobileOtp
                            ? 'Verifying...'
                            : mobileOtpSent
                              ? 'Verify OTP & Continue'
                              : 'Send OTP'}
                        {!sendingMobileOtp && !verifyingMobileOtp && <FiArrowRight />}
                      </button>
                    </div>
                  )}

                  {mobileVerified && !otpVerified && (
                    <div className="apply-mrrupya-send-wrap">
                      <button
                        type="submit"
                        className="apply-mrrupya-btn apply-mrrupya-btn-black"
                        disabled={
                          digitapVerifying ||
                          panChecking ||
                          panLeadBlocked ||
                          (!panVerified && !digitapPending)
                        }
                      >
                        {digitapVerifying
                          ? digitapReturnConfirmed
                            ? 'Confirming Aadhaar...'
                            : digitapPending
                              ? 'Waiting for DigiLocker...'
                              : 'Starting verification...'
                          : 'Continue'}
                        {!digitapVerifying && <FiArrowRight />}
                      </button>
                    </div>
                  )}

                  {otpVerified && (
                    <div className="apply-mrrupya-actions">
                      {submitError && (
                        <div className="apply-mrrupya-submit-error">{submitError}</div>
                      )}
                      <button
                        type="submit"
                        className="apply-mrrupya-btn apply-mrrupya-btn-primary"
                        disabled={submitting || panLeadBlocked || submitted}
                        aria-busy={submitting}
                      >
                        {submitting ? (
                          <>
                            <span className="apply-mrrupya-btn-spinner" aria-hidden="true" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            Submit Application
                            <FiArrowRight />
                          </>
                        )}
                      </button>
                    </div>
                  )}
                  {submitting && (
                    <div className="apply-mrrupya-submit-overlay" aria-live="polite">
                      <div className="apply-mrrupya-submit-overlay-card">
                        <span className="apply-mrrupya-btn-spinner apply-mrrupya-btn-spinner-lg" />
                        <p>Submitting your application…</p>
                        <span>Please wait, do not refresh or click again.</span>
                      </div>
                    </div>
                  )}
                </form>

                <div className="apply-mrrupya-trust-row">
                  <span><FiLock /> 256-bit encryption</span>
                  <span><FiFileText /> No paperwork</span>
                  <span><FiDollarSign /> Zero hidden charges</span>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default ApplyLoan;
 